import type { EditorContext, ToolSettings } from "../tools/EditorContext";
import { markPaidFeature } from "./paidPreview";
import { offsetSourceOutlines } from "../tools/outlineOffsetController";
import type { ShapeKind } from "../tools/ShapeTool";
import type { CutoutKind } from "../tools/CutoutTool";
import type {
  HardwareColor,
  HardwareSide,
  HardwareType,
  Piece,
  Project,
  StitchLine,
} from "../model/Project";
import {
  descendantConstructionGroupIds,
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  groupAncestorIds,
  pieceStitchLines,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  resolvePartStitchingDefaults,
  resolveProjectStitchingDefaults,
  tracingImageOpacity,
  tracingImageRotation,
  type ConstructionGroup,
  type ConstructionLine,
  type TracingImage,
} from "../model/Project";
import type {
  ConstructionKind,
  ConstructionSubMode,
  ConstructionEditAction,
} from "../tools/ConstructionTool";
import { DEFAULT_FREEHAND_SMOOTHNESS } from "../tools/ConstructionTool";
import {
  smoothnessFromPercent,
  smoothnessPercent,
} from "../model/freehandSmoothing";
import { DEFAULT_LEATHER_SMOOTHNESS } from "../model/leatherSmoothing";
import { cycleAnchorBezier } from "../model/constructionAnchors";
import {
  parseSubpathToSegmentList,
  segmentListToPathData,
} from "../model/constructionPathData";
import {
  modifierFor,
  dropConstructionPrimitive,
  rebuildPrimitivePath,
} from "../model/constructionLineNormalize";
import { deleteHandle as pathDeleteHandle } from "../tools/pathDeletion";
import type { StitchKind } from "../tools/StitchTool";
import {
  getUnrotatedBounds,
  normalizeRotation,
  rotateConstructionLine,
  rotatePathData,
} from "../model/constructionRotation";
import {
  allowedRefKindsForConstraint,
  requiredRefsForConstraint,
} from "../model/constraints";
import {
  findBrokenCoincidentConstraints,
  propagateCoincidentAfterAnchorDrag,
} from "../model/constraintEnforcement";
import { constraintIcon } from "../icons/constraintIcons";
import {
  CONNECTIONS,
  type ConnectionRowSpec,
  routeMirrorRotationToSource,
} from "../model/connections";
import { isFeatureEnabled } from "../featureFlags";
import constructionSvgRaw from "../icons/construction.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";
import { isMirrorDerived } from "../model/constructionMirror";
import { showCanvasToast } from "../canvasToast";
import { StitchPanelSection, type StitchPanelHost } from "./stitchPanelSection";
import {
  ConstructionLinePanelSection,
  type ConstructionLinePanelHost,
} from "./constructionLinePanelSection";
import { PiecePanelSection, type PiecePanelHost } from "./piecePanelSection";
import { renderNotePanelSection } from "./notePanelSection";
import { noteCardHeightMm } from "../canvas/noteRenderer";
import { mmInchInputs } from "./mmInchInputs";
import {
  shapeGallery,
  stitchKindGallery,
  constructionGallery,
  slotWidthField,
  cornerRadiusPresetRow,
  holeDiameterField,
  textField,
  presetDimensionField,
  presetIntegerField,
  scrubberInputField,
  presetAngleField,
  lengthField,
  stitchWorkingAreaField,
  stitchStartOfPathField,
  numberField,
  checkbox,
  dimensionsSection,
  panelSection,
  standardSizeList,
} from "./panelFieldBuilders";
import {
  formatAreaMm2,
  formatLengthMm,
  pathAreaMm2,
  pathBoundsAreaMm2,
  pathLengthMm,
  seamLengthMm,
} from "../model/measure";
import { getUnits } from "../preferences";

/**
 * The two area rows for one closed shape (#1056): what it encloses, and the
 * sheet its bounding box takes.
 *
 * Both, not just the first, because the gap between them IS the waste around
 * the part — which is the number that says whether it nests well. Named per
 * kind for the same reason the length rows are: a part and one of its cut-outs
 * read out together, and a bare "Area" wouldn't say whose.
 */
function areaRows(
  kind: string,
  pathData: string,
  scope: paper.PaperScope,
  units: "mm" | "in",
): string[] {
  return [
    `${kind} area ${formatAreaMm2(pathAreaMm2(pathData, scope), units)}`,
    `${kind} bounding box ${formatAreaMm2(pathBoundsAreaMm2(pathData, scope), units)}`,
  ];
}
import {
  standardSizeById,
  standardSizesInContext,
  type StandardSizeContext,
} from "../model/standardSizes";
import {
  circumradiusForSide,
  clampPolygonSides,
  sideForCircumradius,
} from "../model/regularPolygon";
// Re-exported for back-compat: callers that imported `mmInchInputs` from here
// keep working now that it lives in its own (paper-free) module.
export { mmInchInputs };
import type { ConstraintKind, ConstraintRef } from "../model/Project";

/** Construction-line glyph (the Construction tool's icon) for the "Create
 *  construction line" promote button.
 *
 *  It used to run its own private strip and then re-add `width` / `height` /
 *  `fill` — a second normaliser for the same file the toolbar already draws,
 *  which is the drift #216 exists to close. Size and colour are
 *  `.promote-construction-icon` in styles.css now. */
const CONSTRUCTION_LINE_ICON = stripIconSvg(constructionSvgRaw);

/** Bridge the Properties panel uses to drive an "add constraint"
 *  picker that lives in the active tool layer. The panel owns the
 *  palette UI but doesn't know about pointer events / hit-testing —
 *  it asks the bridge to start / cancel / inspect a pick session,
 *  and re-renders whenever `subscribe`'s callback fires. */
export interface ConstraintPickerProvider {
  start: (kind: ConstraintKind, originLineId: string) => void;
  cancel: () => void;
  state: () => {
    kind: ConstraintKind;
    refs: ReadonlyArray<ConstraintRef>;
    originLineId: string;
  } | null;
  subscribe: (fn: () => void) => () => void;
}
import {
  stitchLineLength,
  resolveStitchInsets,
  stitchCardinalTicks,
} from "../model/stitching";
import { deriveStitchMatches } from "../model/stitchMatch";
import type { StitchRef } from "../model/Project";
import { deriveConstructionStitchGrid } from "../model/constructionStitchGrid";
import {
  pickAdjacentDecoration,
  renameConstructionGroup,
} from "../model/projectOps";
import { ungroupConstructionSelection } from "../model/constructionGroupActions";

export type ToolId =
  | "select"
  | "shape"
  | "trace"
  | "region"
  | "hole"
  | "cutout"
  | "stitch"
  | "stamp"
  | "lineStamp"
  | "fill"
  | "freePath"
  | "hardware"
  | "outlineEdit"
  | "leatherMirror"
  | "leatherRound"
  | "leatherSlice"
  | "leatherDelete"
  | "measure"
  | "notes"
  | "construction";

export class PropertiesPanel
  implements StitchPanelHost, ConstructionLinePanelHost, PiecePanelHost
{
  private root: HTMLElement;
  private ctx: EditorContext;
  /** The stitch-line properties section (#865) — this panel OWNS it by
   *  composition and drives it from `render()`; it reaches back through the
   *  `StitchPanelHost` methods below. */
  private stitchSection = new StitchPanelSection(this);
  /** The construction-line properties section (#867) — this panel OWNS it
   *  by composition and drives it from `render()`; it reaches back through
   *  the `ConstructionLinePanelHost` methods below. */
  private constructionSection = new ConstructionLinePanelSection(this);
  /** The leather-piece property panels (#877) — this panel OWNS it by
   *  composition and drives it from `render()`; it reaches back through the
   *  `PiecePanelHost` methods below. */
  private pieceSection = new PiecePanelSection(this);
  private toolId: ToolId = "shape";
  /** Construction-line ids of the in-progress Trace draft (mirrored from the
   *  TraceTool) so the panel can list its segments live while tracing. */
  private traceDraftLineIds: string[] = [];
  /** Arm the "pick a partner stitch" flow for #199 stitch matching. Set from
   *  app.ts; activates the Stitch tool and waits for the next stitch click. */
  private onStitchMatchArm?: (source: StitchRef) => void;
  setStitchMatchArmHandler(fn: (source: StitchRef) => void) {
    this.onStitchMatchArm = fn;
  }

  private shapeKind: ShapeKind = "rect";
  private onShapeKindChange?: (kind: ShapeKind) => void;
  private shapeSlotWidth = 6;
  private onShapeSlotWidthChange?: (width: number) => void;
  /** Locked dimensions for rect / ellipse leather shapes. `null` means
   *  "read from cursor while drawing"; non-null clamps that axis to the
   *  given mm value (and, when both are set, lets a click place a
   *  fixed-size shape without dragging). */
  private shapePresetWidth: number | null = null;
  private shapePresetHeight: number | null = null;
  private onShapePresetWidthChange?: (mm: number | null) => void;
  private onShapePresetHeightChange?: (mm: number | null) => void;
  private cutoutKind: CutoutKind = "rect";
  private onCutoutKindChange?: (kind: CutoutKind) => void;
  private cutoutSlotWidth = 6;
  private onCutoutSlotWidthChange?: (width: number) => void;
  /** Stitch seam kind. `offset` (#768) is the default; the rest mirror the
   *  construction / shape tools: polyline / polygon / line / rect / ellipse /
   *  slot (#766). */
  private stitchKind: StitchKind = "offset";
  private onStitchKindChange?: (kind: StitchKind) => void;
  private stitchSlotWidth = 6;
  private onStitchSlotWidthChange?: (width: number) => void;
  /** Offset seam kind (#768): typed Distance preset (`null` → drag-defined) and
   *  the "single edge" toggle, mirroring the construction offset controls. */
  private stitchPresetOffsetDistance: number | null = null;
  /** Offset shape kind (#972) — typed distance per Draw mode, so the leather
   *  and cutout galleries remember their own value. */
  private shapePresetOffsetDistance: number | null = null;
  private cutoutPresetOffsetDistance: number | null = null;
  private onShapePresetOffsetDistanceChange?: (mm: number | null) => void;
  private onCutoutPresetOffsetDistanceChange?: (mm: number | null) => void;
  private shapeOffsetDistanceSetLive?: (mm: number) => void;
  private cutoutOffsetDistanceSetLive?: (mm: number) => void;
  private onStitchPresetOffsetDistanceChange?: (mm: number | null) => void;
  private stitchSingleSegmentOffset = false;
  private onStitchSingleSegmentOffsetChange?: (on: boolean) => void;
  /** Setter on the live offset Distance input — assigned when the panel renders
   *  the Offset seam kind so the tool's per-frame callback pushes the current
   *  distance into the input (only while no preset is typed). */
  private stitchOffsetDistanceSetLive: ((mm: number) => void) | null = null;
  private cutoutPresetWidth: number | null = null;
  private cutoutPresetHeight: number | null = null;
  private onCutoutPresetWidthChange?: (mm: number | null) => void;
  private onCutoutPresetHeightChange?: (mm: number | null) => void;
  /** Live-drag setters wired in `render()` so the active tool can mirror its
   *  current bounding box into the dimension inputs without a full re-render.
   *  Reset on every render so stale closures from a previous tool / kind are
   *  dropped. `setLive` is a no-op when the user has typed a preset value. */
  private activeWidthSetLive: ((mm: number) => void) | null = null;
  private activeHeightSetLive: ((mm: number) => void) | null = null;
  /** Construction-tool selections — same pattern as the hardware tool. */
  // Mirrors the ConstructionTool's default kind — must match the
  // first tile in the construction kind gallery.
  private constructionKind: ConstructionKind = "rect";
  /** Mirrors the Construction tool's active sub-mode so the panel can
   *  hide its draw-only controls (kind picker, slot width, dimension
   *  inputs) while the user is in Select / Edit sub-modes. */
  private constructionSubMode: ConstructionSubMode = "draw";
  /** Mirror of the Construction tool's `editAction` — needed by
   *  the segment-list dot click so it can dispatch delete vs.
   *  cycle based on which sub-sub-tool is active. */
  private constructionEditAction: ConstructionEditAction = "drag";
  /** Unsubscribe handle for the Scene rotation-live subscription
   *  set up when the rotation scrubber renders. Called from `render`
   *  before clearing the DOM so the stale closure can't fire after
   *  its `<input>` nodes have been removed (issue #67). */
  private rotationLiveUnsubscribe: (() => void) | null = null;
  /** Construction lines whose Constraints section is currently
   *  expanded even though they have no constraints yet. Tracked
   *  per-line id so toggling another line's section doesn't reset
   *  it. Lines with at least one constraint always render the
   *  populated state, regardless of this set. */
  /** Bridge to the active tool's "add constraint" pick session.
   *  Wired up once at app startup (after the Construction tool is
   *  built); the panel uses it to start / cancel a pick and to read
   *  the in-progress ref count for the status block. Optional only
   *  so tests / standalone harnesses can render the panel without
   *  the construction tool present. */
  private constraintPickerProvider: ConstraintPickerProvider | null = null;
  private constraintPickerUnsubscribe: (() => void) | null = null;
  private constructionSlotWidth = 6;
  private constructionPresetWidth: number | null = null;
  private constructionPresetHeight: number | null = null;
  /** Locked length / angle (degrees, visual CCW from horizontal right)
   *  for the Line construction primitive. `null` means the value
   *  follows the cursor live; non-null locks that dimension. */
  private constructionPresetLineLength: number | null = null;
  private constructionPresetLineAngleDeg: number | null = null;
  /** Default smoothness for the freehand Draw tool (issue #416).
   *  Seeded onto the next freehand line; mirrors the per-line
   *  "Smoothness" slider but lives in the Draw tool settings. */
  private constructionPresetFreehandSmoothness = DEFAULT_FREEHAND_SMOOTHNESS;
  /** Default smoothness seeded onto the NEXT freehand part / cutout / seam
   *  (#1016) — the leather twin of the construction Draw-tool preset above.
   *  One value across the three leather tools: they're the same gesture with
   *  the same hand behind it, so a maker who dials in a comfortable amount
   *  shouldn't have to do it three times. */
  private leatherPresetFreehandSmoothness = DEFAULT_LEATHER_SMOOTHNESS;
  /** Live setters for the line-draw length / angle inputs, wired up
   *  in `appendLineDrawInputs`. Reset on every render so a stale
   *  closure from a previous tool / kind doesn't try to update a
   *  removed element. The setters are no-ops while the user has the
   *  field focused (mid-edit) so live cursor updates don't yank the
   *  caret around. */
  private activeLineLengthSetLive: ((mm: number) => void) | null = null;
  private activeLineAngleSetLive: ((deg: number) => void) | null = null;
  /** Live setters for the per-line panel's Length / Angle inputs when
   *  the selected line carries a Line primitive. The Construction
   *  tool's `liveLinePrimitiveCallback` fires these during a vertex
   *  drag so the panel stays in sync with the live geometry. Reset
   *  on every render; the wrappers skip the update when the matching
   *  field is focused (the user is mid-edit). */
  private activeLinePrimitiveLengthSetLive: ((mm: number) => void) | null =
    null;
  private activeLinePrimitiveAngleSetLive: ((deg: number) => void) | null =
    null;
  /** Offset sub-mode preset distance (#29 follow-up). When set, the
   *  tool commits offset lines at this magnitude; the cursor's
   *  side of the source line picks the sign. `null` means "use
   *  whatever distance the drag produces" — in that mode the
   *  input mirrors the live perpendicular distance each frame. */
  private constructionPresetOffsetDistance: number | null = null;
  /** Last value shown in the offset Distance input, used by the
   *  live-mirror callback so the input element can be updated
   *  in-place without re-rendering the whole panel. */
  private constructionLiveOffsetDisplay: number | null = null;
  /** Single-segment offset toggle (issue #681). When on, an offset
   *  drag follows just the ONE edge under the cursor instead of the
   *  whole outline. Holding Alt/Option during the drag does the same
   *  per-gesture. Persists across offset drags within the session. */
  private constructionSingleSegmentOffset = false;
  /** Rounded-corner preference for the LEATHER offset kinds (#423), shared by
   *  the Shape / Cut-out outline offsets and the Stitch edge offset. Defaults
   *  ON, matching `roundedCorners !== false` everywhere else. */
  private leatherOffsetRoundedCorners = true;
  /** Rounded-corner preference for the NEXT offset (issue #423). The
   *  same choice the derived line's own "Round corners" checkbox
   *  makes, moved up onto the tool so the user sets it before drawing
   *  rather than after. Defaults ON, matching the geometric meaning of
   *  a uniform outward expansion (and `roundedCorners !== false`
   *  everywhere else). Applies to both the linked offset and the
   *  detached "simple" one — the simple one bakes the choice into its
   *  standalone geometry, since it has no `offsetSource` to carry it. */
  private constructionOffsetRoundedCorners = true;
  /** Round sub-mode preset corner radius (#100). When set, the next
   *  click on a sharp corner commits at this radius without
   *  requiring a drag — same opt-in pattern as the offset preset.
   *  `null` falls back to drag-defined radius and the input
   *  mirrors the live drag radius each frame. */
  private constructionPresetCornerRadius: number | null = null;
  /** Corner radius (mm) a rectangle is DRAWN with, per tool (#1069) — filled
   *  in by a picked standard size made with rounded corners (a square
   *  coaster), or typed. Distinct from the Round sub-tools' radius, which arms
   *  a click on an EXISTING corner: reusing that one would quietly start
   *  rounding every rectangle drawn after it. */
  private drawCornerRadius = {
    shape: null as number | null,
    cutout: null as number | null,
    construction: null as number | null,
  };
  private onDrawCornerRadiusChange?: (
    scope: "shape" | "cutout" | "construction",
    mm: number | null,
  ) => void;
  /**
   * Which scopes currently have the Standard tile picked (#32).
   *
   * NOT a draw kind: the tool underneath is still drawing a rectangle or an
   * ellipse, and picking a size sets which. Keeping it out of `ConstructionKind`
   * / `ShapeKind` means the draw pipeline is untouched — "standard" is a way of
   * arriving at a size, not a fifth thing to draw.
   */
  private standardMode = { shape: false, cutout: false, construction: false };
  /** The size each scope last picked, so the list can show it selected. */
  private standardPicked = {
    shape: null as string | null,
    cutout: null as string | null,
    construction: null as string | null,
  };
  /** Setter on the live corner-radius input — assigned when the
   *  panel renders the round-corner mode so the tool's per-frame
   *  live callback can push the current radius into the input. */
  private cornerRadiusSetLive: ((mm: number) => void) | null = null;
  private leatherPresetCornerRadius: number | null = null;
  private onLeatherPresetCornerRadiusChange?: (mm: number | null) => void;
  private onConstructionKindChange?: (kind: ConstructionKind) => void;
  private onConstructionSlotWidthChange?: (mm: number) => void;
  private onConstructionPresetWidthChange?: (mm: number | null) => void;
  private onConstructionPresetHeightChange?: (mm: number | null) => void;
  private onConstructionPresetLineLengthChange?: (mm: number | null) => void;
  private onConstructionPresetLineAngleChange?: (deg: number | null) => void;
  private onConstructionPresetFreehandSmoothnessChange?: (v: number) => void;
  private onLeatherPresetFreehandSmoothnessChange?: (v: number) => void;
  private onConstructionPresetOffsetDistanceChange?: (
    mm: number | null,
  ) => void;
  private onConstructionSingleSegmentOffsetChange?: (on: boolean) => void;
  private onLeatherOffsetRoundedCornersChange?: (on: boolean) => void;
  private onConstructionOffsetRoundedCornersChange?: (on: boolean) => void;
  private onConstructionPresetCornerRadiusChange?: (mm: number | null) => void;
  /** Setter on a live offset Distance input — assigned when the
   *  panel renders the offset mode so the tool's per-frame live
   *  callback can push the current distance into the input. */
  private offsetDistanceSetLive: ((mm: number) => void) | null = null;
  /** Setter on the per-line Distance input shown when an offset
   *  construction line is the active selection. Mirrors the
   *  preset-side `offsetDistanceSetLive` so a per-frame distance
   *  update from the tool's Edit-mode offset drag flows into THIS
   *  field too (issue #244 follow-up). */
  private offsetLineDistanceSetLive: ((mm: number) => void) | null = null;
  /** In-progress resize geometry pushed by the Construction tool each frame
   *  (issue #388): the uncommitted, live-resized construction line. While set,
   *  the per-line panel renders this instead of the committed line so the
   *  dimension readouts (width / height / length / angle) track the drag.
   *  Cleared (null) on drag end. */
  private liveConstructionLineOverride: ConstructionLine | null = null;
  /** Per-render transient: id of a stitch line whose user clicked "Override
   *  stitch settings" but hasn't actually changed anything yet. While set,
   *  the line panel renders the editor pre-filled with the line's effective
   *  values. The first real edit promotes the line to an override and clears
   *  this flag. Cleared automatically when the selection leaves the line. */
  private armedLineId: string | null = null;
  /** Snap value at last render — used to detect a snap toggle so the shape /
   *  cutout galleries can relabel "Rectangle/Ellipse" ↔ "Square/Circle". */
  private lastRenderedSnap = false;
  /** Tool-split layout (#339): the tool scope lives in its own sidebar rather
   *  than stacked above the selected object's properties. Owned here but
   *  DRIVEN by app.ts, which holds the preference and the shell's toggle. */
  private toolSplitActive = false;
  private onToolSplitAvailable?: (available: boolean) => void;
  /** Where the tool scope goes when split out — the panel in the separate
   *  sidebar. Null in tests / hosts that don't provide one, which simply
   *  means the panel can never split. */
  private toolRoot: HTMLElement | null = null;
  /** Shift modifier state pushed in by app.ts so drawing-mode tools
   *  can relabel their galleries ("Rectangle" ↔ "Square", "Ellipse"
   *  ↔ "Circle") and dimension inputs the moment Shift is pressed
   *  or released. Pure presentation: the actual size-snap effect is
   *  driven by the PointerSample's `shiftKey` on each drag event. */
  private shiftHeldForDrawing = false;
  /** Alt / Option modifier state pushed in by app.ts — the centre-snap
   *  analogue of `shiftHeldForDrawing`. Pure presentation: while the centre
   *  snap forces the regular-polygon mode, the "Regular polygon" checkbox
   *  reads checked + disabled and the gallery tile flips, so the mode is
   *  visible even with the setting itself off (#934). */
  private altHeldForDrawing = false;
  /** Effective centre-snap state at last render — pairs with
   *  `lastRenderedSnap` to detect modifier / toggle flips that require a
   *  refresh. */
  private lastRenderedCenter = false;

  constructor(root: HTMLElement, ctx: EditorContext, toolRoot?: HTMLElement) {
    this.root = root;
    this.ctx = ctx;
    this.toolRoot = toolRoot ?? null;
    // The shell wraps `#properties-panel` and is `hidden` in the
    // static HTML (issue #181) so the right sidebar reads as "Parts
    // only" at first paint. render() owns its visibility from here on:
    // revealed while either the tool or the selection section has
    // content, hidden again when both are empty (#482).
    this.render();
    // Refresh on snap-toggle so the relabeling lands without a tool switch —
    // but only when the visible panel actually depends on snap, otherwise a
    // refresh would discard input focus elsewhere unnecessarily.
    this.ctx.onSettingsChanged(() => {
      // The "Rectangle / Ellipse" → "Square / Circle" relabel and
      // the single-dimension input collapse depend on whether sizes
      // snap is currently in effect (master ∧ (sizes child OR Shift
      // held in a drawing tool)). Shape, cutout, and construction
      // (Draw sub-mode) all render the snap-dependent gallery +
      // dimension inputs, so any one of them needs a refresh when
      // the effective state flips. Other tools skip the refresh to
      // avoid stealing focus from their own live-commit inputs.
      const snapNow =
        this.ctx.settings.snapMaster &&
        (this.ctx.settings.snapSizes || this.shiftHeldForDrawing);
      // The centre snap forces the regular-polygon mode the same way
      // (checkbox + gallery tile), so a toggle flip (e.g. Alt-Alt lock)
      // needs the refresh too (#934).
      const centerNow =
        this.altHeldForDrawing ||
        (this.ctx.settings.snapMaster && this.ctx.settings.snapCenter);
      if (
        (snapNow !== this.lastRenderedSnap ||
          centerNow !== this.lastRenderedCenter) &&
        (this.toolId === "shape" ||
          this.toolId === "cutout" ||
          this.toolId === "construction")
      ) {
        this.refresh();
      }
    });
    // Keep the panel in sync with the model — every history change
    // (commit / undo / redo) re-runs the panel so labels, names, and
    // gated sub-blocks reflect the new state. We skip the refresh
    // while the user is mid-typing in one of the panel's own inputs
    // so live-commit fields (per-keystroke commits) don't yank the
    // input out from under the cursor on every character.
    this.ctx.history.subscribe(() => {
      if (this.isPanelInputFocused()) return;
      this.render();
    });
  }

  // --- StitchPanelHost -------------------------------------------------
  // Injected services the extracted StitchPanelSection (#865) reaches back
  // into. The shared builders (`updateStitch` / `appendWorkingAreaField` /
  // `promoteButton` / `buildConnectionRow` / `refresh`) are the panel's own
  // methods, widened to public above; these thin accessors expose the panel's
  // append target, context, the "armed line" transient, and the stitch-match
  // arming bridge.
  getRoot(): HTMLElement {
    return this.root;
  }
  getCtx(): EditorContext {
    return this.ctx;
  }
  getArmedLineId(): string | null {
    return this.armedLineId;
  }
  setArmedLineId(id: string | null): void {
    this.armedLineId = id;
  }
  armStitchMatch(source: StitchRef): void {
    this.onStitchMatchArm?.(source);
  }

  // --- ConstructionLinePanelHost ---------------------------------------
  // Injected services the extracted ConstructionLinePanelSection (#867)
  // reaches back into. `getRoot` / `getCtx` / `render` / `buildConnectionRow`
  // are the panel's own (public) members reused as-is; these accessors expose
  // the active sub-mode / edit-action, the constraint-picker bridge, and the
  // per-frame live-drag setter fields (cleared by `render()` each rebuild).
  getConstructionSubMode(): ConstructionSubMode {
    return this.constructionSubMode;
  }
  getConstraintPickerProvider(): ConstraintPickerProvider | null {
    return this.constraintPickerProvider;
  }
  getEffectiveEditAction(): ConstructionEditAction {
    return this.effectiveEditAction();
  }
  setOffsetLineDistanceSetLive(fn: ((mm: number) => void) | null): void {
    this.offsetLineDistanceSetLive = fn;
  }
  setActiveLinePrimitiveLengthSetLive(fn: ((mm: number) => void) | null): void {
    this.activeLinePrimitiveLengthSetLive = fn;
  }
  setActiveLinePrimitiveAngleSetLive(fn: ((deg: number) => void) | null): void {
    this.activeLinePrimitiveAngleSetLive = fn;
  }
  setRotationLiveUnsubscribe(fn: (() => void) | null): void {
    this.rotationLiveUnsubscribe = fn;
  }

  // --- PiecePanelHost --------------------------------------------------
  // Injected services the extracted PiecePanelSection (#877) reaches back
  // into. `getRoot` / `getCtx` / `refresh` / `promoteButton` /
  // `buildConnectionRow` are the panel's own (public) members reused as-is;
  // these accessors expose the in-progress Trace draft's line ids and the
  // stitching-defaults block owned by the stitch section.
  getTraceDraftLineIds(): string[] {
    return this.traceDraftLineIds;
  }
  buildStitchingDefaultsBlock(
    heading: string,
    target:
      | "project"
      | { pieceId: string }
      | { pieceId: string; lineIds: string[] },
  ): HTMLElement {
    return this.stitchSection.buildStitchingDefaultsBlock(heading, target);
  }

  /** True when the active document focus is inside one of this
   *  panel's *text* input controls. Lets `history.subscribe` skip
   *  the re-render that would otherwise blur the live-commit
   *  dimension field on every keystroke. Buttons aren't included —
   *  losing their focus to a re-render is fine, and skipping the
   *  re-render breaks UI like the constraint × that fires a commit
   *  while still focused. */
  /** Shared regular-polygon fields (#928) for every tool with a polygon kind:
   *  Sides (3-60), a set/reset circumscribed Radius preset (like the rect
   *  dimension inputs), and the Equilateral toggle that forces the regular
   *  drag-out without holding the size-snap modifier. All three live in the
   *  shared ToolSettings, so Construction / Shape / Cutout / Stitch stay in
   *  sync. */
  /** Live mirrors for the auto-polygon gesture (#931): show the derived
   *  Sides / Radius while the cursor drags, revert to the stored presets on
   *  commit / cancel. Null when the polygon fields aren't mounted. */
  private polygonLive: {
    sides: (n: number) => void;
    radius: (mm: number) => void;
    sideLength: (mm: number) => void;
    revert: () => void;
  } | null = null;

  updateLivePolygon(sides: number, radiusMm: number): void {
    this.polygonLive?.sides(sides);
    this.polygonLive?.radius(radiusMm);
  }

  /** Stage-1 mirror (#934): the first segment's live length in the Side
   *  length field while the user aims it. */
  updateLivePolygonSideLength(mm: number): void {
    this.polygonLive?.sideLength(mm);
  }

  /** Centre-mode stage-1 mirror (#934): the live radius in the Radius field
   *  while the user drags the circle's radius out. */
  updateLivePolygonRadius(mm: number): void {
    this.polygonLive?.radius(mm);
  }

  clearLivePolygon(): void {
    this.polygonLive?.revert();
  }

  /** Polyline presets (#934 follow-up): a set Segment length pins every
   *  placed segment's length, and a set Angle step snaps each segment's
   *  direction to multiples of it — the same Length / Angle-step pair the
   *  Line kind offers, applied per click-to-add segment. Both write plain
   *  ToolSettings; no panel rebuild on the per-keystroke commits, so the
   *  focused input keeps its focus. */
  private appendPolylineFields() {
    const st = this.ctx.settings;
    const segField = presetDimensionField(
      "Segment length",
      st.polylineSegmentLength,
      (mm) => {
        st.polylineSegmentLength = mm != null && mm > 0 ? mm : null;
        this.ctx.notifySettingsChanged();
      },
    );
    this.root.appendChild(segField.row);
    const angleField = presetAngleField(
      st.polylineAngleDeg,
      (deg) => {
        st.polylineAngleDeg = deg != null && deg > 0 ? deg : null;
        this.ctx.notifySettingsChanged();
      },
      {
        label: "Angle step",
        resetTitle: "Clear the angle step",
      },
    );
    this.root.appendChild(angleField.row);
  }

  private appendPolygonFields() {
    const st = this.ctx.settings;
    // Equilateral leads — Sides / Radius only mean anything in the regular
    // mode, so they render below it and stay disabled until it's on. The
    // checkbox also mirrors a temporarily-forced regular mode (Shift / Alt
    // held or the Size / Centre snap toggle — both force the regular
    // drag-out, #928/#934), same "modifier flips the control" feedback the
    // size chip gives; only a real click writes the setting.
    const sizeForced =
      this.shiftHeldForDrawing || (st.snapMaster && st.snapSizes);
    const centerForced =
      this.altHeldForDrawing || (st.snapMaster && st.snapCenter);
    const equilateralForced = sizeForced || centerForced;
    const regularOn = st.polygonEquilateral || equilateralForced;
    // Side length leads and is NEVER gated — it also pins every segment of
    // the freeform click-to-add polygon (ad-hoc drawing), not just the
    // regular mode (#934).
    // With Sides set, the unset size field advertises its DERIVED value
    // (#934): R = L/(2·sin(π/n)) and back.
    const nSides = st.polygonSides;
    const derivedRadius =
      nSides != null && st.polygonSideLength != null && st.polygonRadius == null
        ? circumradiusForSide(st.polygonSideLength, nSides)
        : null;
    const derivedSideLen =
      nSides != null && st.polygonRadius != null && st.polygonSideLength == null
        ? sideForCircumradius(st.polygonRadius, nSides)
        : null;
    const sideLenField = presetDimensionField(
      "Side length",
      st.polygonSideLength,
      (mm) => {
        st.polygonSideLength = mm != null && mm > 0 ? mm : null;
        // With Sides SET, n + side + radius over-determine the polygon — the
        // last-edited size wins. With Sides unset the pair is meaningful:
        // side length + radius together derive the count (#934) — unless the
        // combination is impossible (the side can't chord the circle even as
        // a triangle: L > 2R·sin(π/3)), in which case the other value resets.
        if (st.polygonSideLength != null && st.polygonRadius != null) {
          const impossible =
            st.polygonSideLength >
            2 * st.polygonRadius * Math.sin(Math.PI / 3) + 1e-9;
          if (st.polygonSides != null || impossible) {
            st.polygonRadius = null;
          } else {
            // Last-edited wins EXACTLY (#934): a rounded integer count can't
            // honour both sizes, so derive the count from the pair and
            // re-derive the OTHER value from it — the freshly typed Side
            // length stays exact and the Radius snaps to its approximation.
            const n = clampPolygonSides(
              Math.PI /
                Math.asin(st.polygonSideLength / (2 * st.polygonRadius)),
            );
            st.polygonRadius = circumradiusForSide(st.polygonSideLength, n);
          }
          // Mirror the recomputed Radius into ITS field only — commits fire
          // per keystroke, and a full panel rebuild here would replace the
          // input mid-typing and steal its focus (#934).
          radiusField.setValue(st.polygonRadius);
        }
        this.ctx.notifySettingsChanged();
      },
      derivedSideLen,
    );
    this.root.appendChild(sideLenField.row);
    const equilateralRow = checkbox(
      "Regular polygon (equilateral)",
      regularOn,
      (v) => {
        st.polygonEquilateral = v;
        this.ctx.notifySettingsChanged();
        this.refresh();
      },
    );
    // While the Size / Centre snap forces the regular mode (modifier held or
    // the toggle on), unchecking would be a no-op — disable the checkbox so
    // the state reads as "held by the snap", not an ignored control.
    if (equilateralForced) {
      const cb = equilateralRow.querySelector("input");
      if (cb) cb.disabled = true;
      equilateralRow.style.opacity = "0.6";
      equilateralRow.title = sizeForced
        ? "Held by the Size snap — release Shift / turn the Size snap off to change"
        : "Held by the Centre snap — release Alt / turn the Centre snap off to change";
    }
    this.root.appendChild(equilateralRow);
    const sides = presetIntegerField(
      "Sides",
      st.polygonSides,
      { min: 3, max: 60, placeholder: "auto" },
      (v) => {
        st.polygonSides = v;
        this.ctx.notifySettingsChanged();
      },
    );
    this.root.appendChild(sides.row);
    const radiusField = presetDimensionField(
      "Radius (circumscribed)",
      st.polygonRadius,
      (mm) => {
        st.polygonRadius = mm != null && mm > 0 ? mm : null;
        // Same rule mirrored — see the Side length handler.
        if (st.polygonRadius != null && st.polygonSideLength != null) {
          const impossible =
            st.polygonSideLength >
            2 * st.polygonRadius * Math.sin(Math.PI / 3) + 1e-9;
          if (st.polygonSides != null || impossible) {
            st.polygonSideLength = null;
          } else {
            // Mirror of the Side length handler (#934): the freshly typed
            // Radius stays exact; the Side length snaps to the integer
            // count's chord.
            const n = clampPolygonSides(
              Math.PI /
                Math.asin(st.polygonSideLength / (2 * st.polygonRadius)),
            );
            st.polygonSideLength = sideForCircumradius(st.polygonRadius, n);
          }
          // Targeted update, same reasoning as the Side length handler —
          // never rebuild the panel from a per-keystroke commit (#934).
          sideLenField.setValue(st.polygonSideLength);
        }
        this.ctx.notifySettingsChanged();
      },
      derivedRadius,
    );
    this.root.appendChild(radiusField.row);
    this.polygonLive = {
      sides: (n) => sides.setLive(n),
      radius: (mm) => radiusField.setLive(mm),
      sideLength: (mm) => sideLenField.setLive(mm),
      revert: () => {
        sides.setValue(st.polygonSides);
        radiusField.setValue(st.polygonRadius);
        sideLenField.setValue(st.polygonSideLength);
      },
    };
    // Re-render once typing ends (focus leaves the polygon fields) so the
    // gating + derived placeholders catch up — never mid-keystroke, which
    // would replace the focused input and interrupt the user (#934).
    for (const r of [sideLenField.row, sides.row, radiusField.row]) {
      r.addEventListener("focusout", () => {
        setTimeout(() => {
          if (!this.isPanelInputFocused()) this.refresh();
        }, 0);
      });
    }
    if (!regularOn) {
      for (const row of [sides.row, radiusField.row]) {
        row.style.opacity = "0.45";
        row
          .querySelectorAll<
            HTMLInputElement | HTMLButtonElement
          >("input, button")
          .forEach((el) => {
            el.disabled = true;
          });
      }
    }
    const help = document.createElement("div");
    help.style.color = "var(--text-dim)";
    help.style.fontSize = "11px";
    help.style.marginTop = "4px";
    help.textContent =
      "With Equilateral (or the Size snap) on, drag the first side — with Sides unset the count follows your cursor (auto). Set Sides to pin the count, Radius to pin the size; Center snap / Alt drags from the center.";
    this.root.appendChild(help);
  }

  private isPanelInputFocused(): boolean {
    const active = document.activeElement;
    if (!active || active === document.body) return false;
    // The tool scope may be living in the other sidebar (#339), so "in the
    // panel" means either root — otherwise a refresh would wipe a field the
    // user is typing in the moment it's split out.
    if (!this.root.contains(active) && !this.toolRoot?.contains(active))
      return false;
    const tag = active.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA") return true;
    return active instanceof HTMLElement && active.isContentEditable;
  }

  setShapeKindHandler(fn: (kind: ShapeKind) => void) {
    this.onShapeKindChange = fn;
  }

  setShapeSlotWidthHandler(fn: (width: number) => void) {
    this.onShapeSlotWidthChange = fn;
  }

  setStitchKindHandler(fn: (kind: StitchKind) => void) {
    this.onStitchKindChange = fn;
  }

  setStitchSlotWidthHandler(fn: (width: number) => void) {
    this.onStitchSlotWidthChange = fn;
  }

  setStitchPresetOffsetDistanceHandler(fn: (mm: number | null) => void) {
    this.onStitchPresetOffsetDistanceChange = fn;
  }

  setStitchSingleSegmentOffsetHandler(fn: (on: boolean) => void) {
    this.onStitchSingleSegmentOffsetChange = fn;
  }

  /** Push a live offset distance from the Stitch tool's per-frame callback into
   *  the Offset kind's Distance input (no-op if that field isn't shown). */
  setStitchLiveOffsetDistance(mm: number): void {
    this.stitchOffsetDistanceSetLive?.(mm);
  }

  setShapePresetOffsetDistanceHandler(fn: (mm: number | null) => void) {
    this.onShapePresetOffsetDistanceChange = fn;
  }

  setCutoutPresetOffsetDistanceHandler(fn: (mm: number | null) => void) {
    this.onCutoutPresetOffsetDistanceChange = fn;
  }

  /** Live mirrors for the leather / cutout Offset shape kind (#972) — the twin
   *  of `setStitchLiveOffsetDistance`. */
  setShapeLiveOffsetDistance(mm: number): void {
    this.shapeOffsetDistanceSetLive?.(mm);
  }

  setCutoutLiveOffsetDistance(mm: number): void {
    this.cutoutOffsetDistanceSetLive?.(mm);
  }

  /** Why the Offset tile is unavailable, or null when it's usable (#972).
   *  Offset derives a shape from an outline that already exists, so on an empty
   *  project — or one whose only outlines are construction-linked, and so
   *  re-derived rather than offsettable — there is nothing to start from. Uses
   *  the SAME source rule the gesture uses, so the tile can't claim to be
   *  usable when a press would find no edge. */
  /** The tool-level "Smoothness" field shown while a leather FREEHAND kind is
   *  picked (#1016) — the initial value a newly traced part / cutout / seam is
   *  created with, the same way the per-object field re-fits one afterwards. */
  private appendLeatherFreehandSmoothness(): void {
    this.root.appendChild(
      numberField(
        "Smoothness (%)",
        smoothnessPercent(this.leatherPresetFreehandSmoothness),
        0,
        100,
        1,
        (pct) => {
          const v = smoothnessFromPercent(pct);
          this.leatherPresetFreehandSmoothness = v;
          this.onLeatherPresetFreehandSmoothnessChange?.(v);
        },
      ),
    );
  }

  private offsetDisabledReason(): string | null {
    return offsetSourceOutlines(this.ctx.history.state).length > 0
      ? null
      : "Offset needs an existing part or cutout outline to offset from";
  }

  /** Render the Offset shape kind's Distance input + help text (#972). Shared
   *  by the leather and cutout branches so the two can't drift; `insetOnly`
   *  reflects the mode's direction rule and only changes the wording. */
  /** The shared "Round corners" tick for every LEATHER offset kind (#423) —
   *  part / cut-out outline offsets and the stitch edge-offset seam. One
   *  preference across the three tools, the way the freehand smoothness preset
   *  is shared: it is a choice about the shape you want, not about which tool
   *  you happen to be holding. Construction keeps its own copy, since its
   *  offsets are re-derivable objects with the same tick on the finished line.
   */
  private buildOffsetRoundCornersToggle(): HTMLElement {
    const row = checkbox(
      "Round corners",
      this.leatherOffsetRoundedCorners,
      (on) => {
        this.leatherOffsetRoundedCorners = on;
        this.onLeatherOffsetRoundedCornersChange?.(on);
      },
    );
    // The bisector offset that keeps corners square only applies to an
    // all-straight outline; say so rather than let the user wonder why a
    // rounded part ignored the tick.
    row.title =
      "Outward offsets of an outline with sharp corners get an arc of the offset distance at each corner.";
    return row;
  }

  private appendOffsetKindFields(opts: {
    value: number | null;
    onChange: (mm: number | null) => void;
    setLive: (fn: (mm: number) => void) => void;
    insetOnly: boolean;
  }): void {
    const field = presetDimensionField("Distance", opts.value, opts.onChange);
    opts.setLive(field.setLive);
    this.root.appendChild(field.row);
    this.root.appendChild(this.buildOffsetRoundCornersToggle());
    const help = document.createElement("div");
    help.style.color = "var(--text-dim)";
    help.style.fontSize = "11px";
    help.style.marginTop = "4px";
    // Deliberately NOT value-dependent: re-rendering the panel to reword this
    // would tear down and rebuild the Distance input on every commit, which
    // destroys focus mid-typing (you could not finish typing "1/4"). The text
    // covers both states instead, and the field is left alone.
    help.textContent = opts.insetOnly
      ? "Drag from a part or cutout edge inward; the drag distance sets the offset. Or type a Distance and click an edge to place it there."
      : "Drag from a part or cutout edge; the drag distance sets the offset. Or type a Distance and click an edge to place it there (drag a little to pick the side).";
    this.root.appendChild(help);
  }

  setShapePresetWidthHandler(fn: (mm: number | null) => void) {
    this.onShapePresetWidthChange = fn;
  }

  setShapePresetHeightHandler(fn: (mm: number | null) => void) {
    this.onShapePresetHeightChange = fn;
  }

  setCutoutPresetWidthHandler(fn: (mm: number | null) => void) {
    this.onCutoutPresetWidthChange = fn;
  }

  setCutoutPresetHeightHandler(fn: (mm: number | null) => void) {
    this.onCutoutPresetHeightChange = fn;
  }

  /** Called by the active tool while a rect/ellipse drag is in progress so
   *  the panel's width / height inputs can mirror the bounding box. The
   *  underlying setLive helpers are no-ops when a preset value is locked,
   *  so the locked axis stays put even mid-drag. */
  updateLiveDimensions(width: number, height: number) {
    this.activeWidthSetLive?.(width);
    this.activeHeightSetLive?.(height);
  }

  /** Clears any locked shape / cutout dimension presets and notifies the
   *  tools so they drop their internal preset state too. Called from the
   *  project Reset button so a new project doesn't carry over dimensions
   *  the user had locked for the previous one. */
  clearAllPresets() {
    if (this.shapePresetWidth !== null) {
      this.shapePresetWidth = null;
      this.onShapePresetWidthChange?.(null);
    }
    if (this.shapePresetHeight !== null) {
      this.shapePresetHeight = null;
      this.onShapePresetHeightChange?.(null);
    }
    if (this.cutoutPresetWidth !== null) {
      this.cutoutPresetWidth = null;
      this.onCutoutPresetWidthChange?.(null);
    }
    if (this.cutoutPresetHeight !== null) {
      this.cutoutPresetHeight = null;
      this.onCutoutPresetHeightChange?.(null);
    }
    if (this.constructionPresetLineLength !== null) {
      this.constructionPresetLineLength = null;
      this.onConstructionPresetLineLengthChange?.(null);
    }
    if (this.constructionPresetLineAngleDeg !== null) {
      this.constructionPresetLineAngleDeg = null;
      this.onConstructionPresetLineAngleChange?.(null);
    }
    // Re-render so any currently-mounted preset input picks up the cleared
    // state — handlers above already pushed the change to the tools.
    this.refresh();
  }

  setCutoutKindHandler(fn: (kind: CutoutKind) => void) {
    this.onCutoutKindChange = fn;
  }

  setCutoutSlotWidthHandler(fn: (width: number) => void) {
    this.onCutoutSlotWidthChange = fn;
  }

  setHardwareTypeHandler(fn: (type: HardwareType) => void) {
    this.pieceSection.setHardwareTypeHandler(fn);
  }
  setHardwareColorHandler(fn: (color: HardwareColor) => void) {
    this.pieceSection.setHardwareColorHandler(fn);
  }
  setHardwareSizeHandler(fn: (mm: number) => void) {
    this.pieceSection.setHardwareSizeHandler(fn);
  }
  /** Driven by app.ts whenever the construction sub-tool changes so
   *  the panel can hide its draw-only controls outside Draw mode. */
  setConstructionSubMode(mode: ConstructionSubMode) {
    if (this.constructionSubMode === mode) return;
    this.constructionSubMode = mode;
    if (this.toolId === "construction") this.refresh();
  }
  /** Mirror the Construction tool's editAction so the segment-list
   *  dot click can dispatch correctly (delete vs cycle). Triggers
   *  a panel refresh so any visual that depends on the current
   *  edit-action stays in sync. */
  setConstructionEditAction(action: ConstructionEditAction) {
    if (this.constructionEditAction === action) return;
    this.constructionEditAction = action;
    if (this.toolId === "construction") this.refresh();
  }

  /** The edit action the segment-list dot click should honour. The
   *  Delete sub-tool is its own top-level sub-mode now (issue #670
   *  follow-up), so treat it as the "delete" action regardless of the
   *  Edit-only `constructionEditAction`. */
  private effectiveEditAction(): ConstructionEditAction {
    return this.constructionSubMode === "delete"
      ? "delete"
      : this.constructionEditAction;
  }

  setConstructionKindHandler(fn: (kind: ConstructionKind) => void) {
    this.onConstructionKindChange = fn;
  }
  /** Set the currently-selected Construction Draw kind. Used at
   *  startup to seed the panel from a persisted preference so the
   *  user lands on the kind they were drawing before the reload,
   *  not the hard-coded default. */
  setConstructionKind(kind: ConstructionKind) {
    if (this.constructionKind === kind) return;
    this.constructionKind = kind;
    if (this.toolId === "construction") this.refresh();
  }
  /** Mirror a restored Draw kind into the picker (#972), the leather twins of
   *  `setConstructionKind` above — so the gallery highlights the kind restored
   *  from the tool preference on first paint rather than the hard-coded
   *  default. Persistence itself lives in app.ts with the other PREF_* keys;
   *  the panel stays unaware of storage. */
  setShapeKind(kind: ShapeKind) {
    if (this.shapeKind === kind) return;
    this.shapeKind = kind;
    if (this.toolId === "shape") this.refresh();
  }

  setCutoutKind(kind: CutoutKind) {
    if (this.cutoutKind === kind) return;
    this.cutoutKind = kind;
    if (this.toolId === "cutout") this.refresh();
  }

  setStitchKind(kind: StitchKind) {
    if (this.stitchKind === kind) return;
    this.stitchKind = kind;
    if (this.toolId === "stitch") this.refresh();
  }

  setConstructionSlotWidthHandler(fn: (mm: number) => void) {
    this.onConstructionSlotWidthChange = fn;
  }
  setConstructionPresetWidthHandler(fn: (mm: number | null) => void) {
    this.onConstructionPresetWidthChange = fn;
  }
  setConstructionPresetHeightHandler(fn: (mm: number | null) => void) {
    this.onConstructionPresetHeightChange = fn;
  }
  setConstructionPresetLineLengthHandler(fn: (mm: number | null) => void) {
    this.onConstructionPresetLineLengthChange = fn;
  }
  setConstructionPresetLineAngleHandler(fn: (deg: number | null) => void) {
    this.onConstructionPresetLineAngleChange = fn;
  }
  setConstructionPresetFreehandSmoothnessHandler(fn: (v: number) => void) {
    this.onConstructionPresetFreehandSmoothnessChange = fn;
  }
  setLeatherPresetFreehandSmoothnessHandler(fn: (v: number) => void) {
    this.onLeatherPresetFreehandSmoothnessChange = fn;
  }
  /** Seed the leather freehand-smoothness preset from the persisted preference
   *  at startup (#1016). */
  setLeatherPresetFreehandSmoothness(v: number) {
    this.leatherPresetFreehandSmoothness = v;
    if (
      this.toolId === "shape" ||
      this.toolId === "cutout" ||
      this.toolId === "stitch"
    ) {
      this.refresh();
    }
  }
  /** Seed the panel's freehand-smoothness preset from a persisted
   *  preference at startup (issue #416), mirroring
   *  {@link setConstructionKind}. */
  setConstructionPresetFreehandSmoothness(v: number) {
    this.constructionPresetFreehandSmoothness = v;
    if (this.toolId === "construction") this.refresh();
  }
  setConstructionPresetOffsetDistanceHandler(fn: (mm: number | null) => void) {
    this.onConstructionPresetOffsetDistanceChange = fn;
  }
  setConstructionSingleSegmentOffsetHandler(fn: (on: boolean) => void) {
    this.onConstructionSingleSegmentOffsetChange = fn;
  }
  setConstructionOffsetRoundedCornersHandler(fn: (on: boolean) => void) {
    this.onConstructionOffsetRoundedCornersChange = fn;
  }
  setLeatherOffsetRoundedCornersHandler(fn: (on: boolean) => void) {
    this.onLeatherOffsetRoundedCornersChange = fn;
  }
  setConstructionPresetCornerRadiusHandler(fn: (mm: number | null) => void) {
    this.onConstructionPresetCornerRadiusChange = fn;
  }

  /** Leather Round method (#974) — same opt-in contract as the construction
   *  Round sub-mode's radius: a typed / picked value locks the next corner
   *  click to that radius, an empty one falls back to the drag. */
  /** Where a draw-time corner radius goes (#1069): the tool of that scope. */
  setDrawCornerRadiusHandler(
    fn: (scope: "shape" | "cutout" | "construction", mm: number | null) => void,
  ) {
    this.onDrawCornerRadiusChange = fn;
  }

  private setDrawCornerRadius(
    scope: "shape" | "cutout" | "construction",
    mm: number | null,
  ) {
    this.drawCornerRadius[scope] = mm;
    this.onDrawCornerRadiusChange?.(scope, mm);
  }

  /** The "Corner radius" a rectangle is drawn with (#1069) — blank draws
   *  sharp corners. */
  private appendDrawCornerRadiusField(
    scope: "shape" | "cutout" | "construction",
  ) {
    const field = presetDimensionField(
      "Corner radius",
      this.drawCornerRadius[scope],
      (mm) => this.setDrawCornerRadius(scope, mm),
    );
    field.row.dataset.role = "draw-corner-radius";
    this.root.appendChild(field.row);
  }

  setLeatherPresetCornerRadiusHandler(fn: (mm: number | null) => void) {
    this.onLeatherPresetCornerRadiusChange = fn;
  }

  /** Push a live corner radius from the Construction tool into the
   *  panel's currently-rendered input (no full re-render). Called
   *  by the tool's per-frame `liveCornerRadiusCallback` so the user
   *  can read the radius while dragging without us re-rendering. */
  setLiveCornerRadius(mm: number): void {
    this.cornerRadiusSetLive?.(mm);
  }

  /** Push the in-progress resize geometry (issue #388) so the per-line panel's
   *  dimension readouts track the drag live. Pass the uncommitted line while
   *  resizing, or null on drag end to fall back to the committed geometry.
   *  Re-renders the panel; safe because a canvas drag never holds focus in a
   *  panel input. */
  setLiveConstructionLine(line: ConstructionLine | null): void {
    if (this.liveConstructionLineOverride === null && line === null) return;
    this.liveConstructionLineOverride = line;
    this.refresh();
  }

  /** Push the global Shift modifier state. Called from app.ts when
   *  Shift is pressed or released and the active tool is a drawing
   *  tool (Shape / Cutout / Construction Draw). Triggers a refresh
   *  if the effective size-snap state changed (so "Rectangle" ↔
   *  "Square" gallery labels and dimension inputs flip live with
   *  the modifier). Tools that don't care about this state (Stitch,
   *  Hardware, Stamp, etc.) get this call too but the formula
   *  short-circuits to a no-op. */
  setShiftHeldForDrawing(held: boolean): void {
    if (this.shiftHeldForDrawing === held) return;
    this.shiftHeldForDrawing = held;
    const s = this.ctx.settings;
    const snapNow = s.snapMaster && (s.snapSizes || held);
    if (
      snapNow !== this.lastRenderedSnap &&
      (this.toolId === "shape" ||
        this.toolId === "cutout" ||
        this.toolId === "stitch" ||
        this.toolId === "construction")
    ) {
      this.refresh();
    }
  }

  /** Push the global Alt / Option modifier state — the centre-snap analogue
   *  of {@link setShiftHeldForDrawing} (#934). While held in a drawing tool
   *  the regular-polygon mode is forced, so the "Regular polygon" checkbox
   *  flips to checked + disabled and the polygon gallery tile swaps to its
   *  regular form, live with the modifier. */
  setAltHeldForDrawing(held: boolean): void {
    if (this.altHeldForDrawing === held) return;
    this.altHeldForDrawing = held;
    const s = this.ctx.settings;
    const centerNow = held || (s.snapMaster && s.snapCenter);
    if (
      centerNow !== this.lastRenderedCenter &&
      (this.toolId === "shape" ||
        this.toolId === "cutout" ||
        this.toolId === "stitch" ||
        this.toolId === "construction")
    ) {
      this.refresh();
    }
  }

  /** Called by the Construction tool while a Line draw is in
   *  progress so the panel's length / angle inputs can mirror the
   *  live cursor position. No-ops when a preset value is locked or
   *  when the user is mid-typing in the field. */
  updateLiveLine(lengthMm: number, angleDeg: number) {
    this.activeLineLengthSetLive?.(lengthMm);
    this.activeLineAngleSetLive?.(angleDeg);
  }

  /** Called by the Construction tool every frame while the user
   *  drags a vertex of a *selected* Line primitive on the canvas.
   *  Pushes the live length + visual-angle into the per-line panel's
   *  Length / Angle inputs so the readout tracks the in-progress
   *  geometry without waiting for the drag-commit. No-op when the
   *  per-line line-primitive section isn't currently rendered (the
   *  selection moved off, or the line lost its primitive). */
  updateLineLivePrimitive(lengthMm: number, angleDeg: number) {
    this.activeLinePrimitiveLengthSetLive?.(lengthMm);
    this.activeLinePrimitiveAngleSetLive?.(angleDeg);
  }

  /** Push a live offset distance from the Construction tool into the
   *  panel's currently-rendered input (no full re-render). Called by
   *  the tool's per-frame `liveOffsetDistanceCallback` so the user
   *  can read the perpendicular distance while dragging without us
   *  having to refresh the whole properties panel each frame. */
  setLiveOffsetDistance(mm: number): void {
    this.constructionLiveOffsetDisplay = mm;
    this.offsetDistanceSetLive?.(mm);
    this.offsetLineDistanceSetLive?.(mm);
  }

  setHardwareSideHandler(fn: (side: HardwareSide) => void) {
    this.pieceSection.setHardwareSideHandler(fn);
  }

  /** Wire the panel to an "add constraint" picker in the active
   *  tool. Called once at app startup. The panel subscribes so
   *  picker state changes (start, ref captured, commit, cancel)
   *  re-render the constraints section without a full history
   *  refresh. */
  setConstraintPickerProvider(provider: ConstraintPickerProvider) {
    this.constraintPickerUnsubscribe?.();
    this.constraintPickerProvider = provider;
    this.constraintPickerUnsubscribe = provider.subscribe(() => {
      // Always refresh on picker ticks — these fire from canvas
      // taps and from the in-panel Cancel button (which itself
      // becomes the focus), so the input-focus guard used by the
      // history subscription would suppress the necessary re-render.
      // The picker doesn't co-exist with text-input editing anyway:
      // arming a pick blocks chip clicks elsewhere and the section
      // doesn't host any live-commit fields.
      this.refresh();
    });
  }

  setTool(id: ToolId) {
    this.toolId = id;
    this.render();
  }

  /** Push the in-progress Trace draft's referenced construction-line ids so the
   *  panel lists its segments while tracing (#650). Re-renders only when the
   *  Trace tool is active. */
  setTraceDraft(lineIds: string[]) {
    this.traceDraftLineIds = lineIds;
    if (this.toolId === "trace") this.render();
  }

  /** Display name for the panel title — the most specific tool the user
   *  has picked (#482). For every tool group except Construction the
   *  `toolId` already IS the subtool; Construction's `toolId` is the
   *  top-level tool, so its active sub-mode supplies the name. The
   *  "simple" offset / mirror sub-modes are the plain user-facing
   *  actions and their live twins are the wand-badged "modifier"
   *  variants, matching the subtoolbar tooltips. */
  private activeToolLabel(): string {
    if (this.toolId === "construction") {
      const subModeLabels: Record<ConstructionSubMode, string> = {
        draw: "Draw",
        select: "Select",
        edit: "Edit",
        "simple-offset": "Offset",
        offset: "Offset modifier",
        "simple-mirror": "Mirror",
        mirror: "Mirror modifier",
        round: "Round",
        delete: "Delete",
      };
      return subModeLabels[this.constructionSubMode];
    }
    const toolLabels: Record<Exclude<ToolId, "construction">, string> = {
      notes: "Notes",
      select: "Select",
      shape: "Shape",
      trace: "Trace",
      region: "Region",
      hole: "Hole",
      cutout: "Cutout",
      stitch: "Stitch",
      stamp: "Stamp",
      lineStamp: "Line stamp",
      fill: "Fill",
      freePath: "Free path",
      hardware: "Hardware",
      outlineEdit: "Edit points",
      leatherMirror: "Mirror",
      leatherRound: "Round corners",
      leatherSlice: "Slice",
      leatherDelete: "Delete",
      measure: "Measure",
    };
    return toolLabels[this.toolId];
  }

  refresh() {
    this.render();
  }

  render() {
    // Drop the armed-override flag if the selection has moved off the line
    // it was attached to — the user has navigated away without committing.
    const selNow = this.ctx.scene.getSelection();
    if (this.armedLineId && selNow.stitchId !== this.armedLineId) {
      this.armedLineId = null;
    }
    const s = this.ctx.settings;
    // Effective size-snap for label/galleries — persistent setting
    // OR Shift held by a drawing tool (Shape / Cutout / Construction
    // Draw). Tracked here so the relabel pass below can detect a
    // change driven by Shift alone, without a settings event.
    this.lastRenderedSnap =
      s.snapMaster && (s.snapSizes || this.shiftHeldForDrawing);
    this.lastRenderedCenter =
      this.altHeldForDrawing || (s.snapMaster && s.snapCenter);
    // Drop stale live-dim setters from the previous render — the next tool /
    // kind may not have dim inputs at all, and we don't want a lingering
    // closure trying to update a removed element.
    this.activeWidthSetLive = null;
    this.activeHeightSetLive = null;
    this.activeLineLengthSetLive = null;
    this.activeLineAngleSetLive = null;
    this.activeLinePrimitiveLengthSetLive = null;
    this.activeLinePrimitiveAngleSetLive = null;
    this.offsetDistanceSetLive = null;
    this.offsetLineDistanceSetLive = null;
    this.stitchOffsetDistanceSetLive = null;
    // Drop any rotation-live subscription from the previous render —
    // its closure captured DOM nodes that we're about to wipe.
    this.rotationLiveUnsubscribe?.();
    this.rotationLiveUnsubscribe = null;
    this.root.innerHTML = "";
    const title = document.createElement("h3");
    // Title prefix is "Tool" rather than "Properties" so the panel's
    // top-level heading reads as the **active drawing tool** rather
    // than a generic properties bucket (issue #334). Selection-driven
    // sub-panels (per-line, per-piece, etc.) get appended below with
    // their own headings, so the "object properties" half of the
    // panel is distinct from the "tool" half at a glance.
    //
    // The label is the MOST SPECIFIC tool the user has picked, not the
    // top-level toolbar entry (#482): `toolId` is already the subtool
    // for the leather / stamping groups ("shape", "cutout", "stamp",
    // …), but for Construction it's the parent tool — there the active
    // sub-mode ("Draw", "Round", …) is what the panel's controls
    // actually belong to.
    title.textContent = "Tool — " + this.activeToolLabel();
    this.root.appendChild(title);

    // The leather / cutout / stitch mode picker moved out of the panel and
    // into the Draw / Trace / Fill sub-sub-tools in the toolbar (#741).
    if (this.toolId === "trace") {
      this.pieceSection.appendTraceDraftSection();
    }

    // Snap mode constrains corner-drags to a 1:1 aspect ratio, so a rectangle
    // becomes a square and an ellipse becomes a circle. Relabel the choices
    // while snap is on so the UI matches what the tool will actually draw.
    // Shift held in a drawing tool counts the same — pressing Shift mid-
    // session should flip "Rectangle" → "Square" instantly so the gallery
    // and the live preview agree.
    const snapOn = s.snapMaster && (s.snapSizes || this.shiftHeldForDrawing);
    // Regular-polygon drag-out active (#928/#934): the size snap, the centre
    // snap, or the shared Equilateral toggle turns the polygon kind into the
    // regular n-gon drag.
    const polygonRegular =
      snapOn ||
      this.altHeldForDrawing ||
      (s.snapMaster && s.snapCenter) ||
      s.polygonEquilateral;
    if (this.toolId === "shape") {
      this.root.appendChild(
        shapeGallery(
          this.standardMode.shape ? "standard" : this.shapeKind,
          (v) => {
            if (v === "standard") {
              this.standardMode.shape = true;
              this.refresh();
              return;
            }
            this.standardMode.shape = false;
            this.shapeKind = v;
            this.onShapeKindChange?.(v);
            this.refresh();
          },
          snapOn,
          "Pill",
          polygonRegular,
          this.offsetDisabledReason(),
        ),
      );
      if (this.shapeKind === "offset") {
        this.appendOffsetKindFields({
          value: this.shapePresetOffsetDistance,
          onChange: (mm) => {
            this.shapePresetOffsetDistance = mm;
            this.onShapePresetOffsetDistanceChange?.(mm);
          },
          setLive: (fn) => (this.shapeOffsetDistanceSetLive = fn),
          insetOnly: false,
        });
      }
      if (this.shapeKind === "slot") {
        this.root.appendChild(
          slotWidthField(
            this.shapeSlotWidth,
            (newWidth) => {
              this.shapeSlotWidth = newWidth;
              this.onShapeSlotWidthChange?.(newWidth);
              this.refresh();
            },
            "Pill width (mm) — drag to set length",
          ),
        );
      }
      if (this.shapeKind === "polygon") {
        this.appendPolygonFields();
      }
      if (this.shapeKind === "freehand") {
        this.appendLeatherFreehandSmoothness();
      }
      if (this.standardMode.shape) {
        this.appendStandardSizePanel({
          scope: "shape",
          // Only things you'd cut WHOLE from leather. A part shaped like a
          // banknote is not a thing; a slot for one is, and that is a cut-out.
          context: "leatherPiece",
          setKind: (k) => {
            this.shapeKind = k;
            this.onShapeKindChange?.(k);
          },
          getWidth: () => this.shapePresetWidth,
          getHeight: () => this.shapePresetHeight,
          setWidth: (mm) => {
            this.shapePresetWidth = mm;
            this.onShapePresetWidthChange?.(mm);
          },
          setHeight: (mm) => {
            this.shapePresetHeight = mm;
            this.onShapePresetHeightChange?.(mm);
          },
        });
      } else if (this.shapeKind === "rect" || this.shapeKind === "ellipse") {
        this.appendDimensionInputs({
          isEllipse: this.shapeKind === "ellipse",
          snapOn,
          getWidth: () => this.shapePresetWidth,
          getHeight: () => this.shapePresetHeight,
          setWidth: (mm: number | null) => {
            this.shapePresetWidth = mm;
            this.onShapePresetWidthChange?.(mm);
          },
          setHeight: (mm: number | null) => {
            this.shapePresetHeight = mm;
            this.onShapePresetHeightChange?.(mm);
          },
          cornerRadiusScope: "shape",
        });
      }
    }
    if (this.toolId === "cutout") {
      this.root.appendChild(
        shapeGallery(
          this.standardMode.cutout ? "standard" : this.cutoutKind,
          (v) => {
            if (v === "standard") {
              this.standardMode.cutout = true;
              this.refresh();
              return;
            }
            this.standardMode.cutout = false;
            this.cutoutKind = v;
            this.onCutoutKindChange?.(v);
            this.refresh();
          },
          snapOn,
          "Oblong",
          polygonRegular,
          this.offsetDisabledReason(),
        ),
      );
      if (this.cutoutKind === "offset") {
        this.appendOffsetKindFields({
          value: this.cutoutPresetOffsetDistance,
          onChange: (mm) => {
            this.cutoutPresetOffsetDistance = mm;
            this.onCutoutPresetOffsetDistanceChange?.(mm);
          },
          setLive: (fn) => (this.cutoutOffsetDistanceSetLive = fn),
          // Offsetting a cutout FROM a part outline can only inset; from
          // another cutout both ways are legal, so the wording stays general.
          insetOnly: false,
        });
      }
      if (this.cutoutKind === "slot") {
        this.root.appendChild(
          slotWidthField(this.cutoutSlotWidth, (newWidth) => {
            this.cutoutSlotWidth = newWidth;
            this.onCutoutSlotWidthChange?.(newWidth);
            this.refresh();
          }),
        );
      }
      if (this.cutoutKind === "polygon") {
        this.appendPolygonFields();
      }
      if (this.cutoutKind === "freehand") {
        this.appendLeatherFreehandSmoothness();
      }
      if (this.standardMode.cutout) {
        this.appendStandardSizePanel({
          scope: "cutout",
          // The full list: a cut-out is the slot or pocket an object goes into,
          // which is exactly what a card, a note or a tracker is here for.
          context: "cutout",
          setKind: (k) => {
            this.cutoutKind = k;
            this.onCutoutKindChange?.(k);
          },
          getWidth: () => this.cutoutPresetWidth,
          getHeight: () => this.cutoutPresetHeight,
          setWidth: (mm) => {
            this.cutoutPresetWidth = mm;
            this.onCutoutPresetWidthChange?.(mm);
          },
          setHeight: (mm) => {
            this.cutoutPresetHeight = mm;
            this.onCutoutPresetHeightChange?.(mm);
          },
        });
      } else if (this.cutoutKind === "rect" || this.cutoutKind === "ellipse") {
        this.appendDimensionInputs({
          isEllipse: this.cutoutKind === "ellipse",
          snapOn,
          getWidth: () => this.cutoutPresetWidth,
          getHeight: () => this.cutoutPresetHeight,
          setWidth: (mm: number | null) => {
            this.cutoutPresetWidth = mm;
            this.onCutoutPresetWidthChange?.(mm);
          },
          setHeight: (mm: number | null) => {
            this.cutoutPresetHeight = mm;
            this.onCutoutPresetHeightChange?.(mm);
          },
          cornerRadiusScope: "cutout",
        });
      }
    }
    // Wrap every settings mutation so subscribed tools (e.g., StampTool's
    // hover ghost) can refresh in response.
    const notify =
      <T>(set: (v: T) => void) =>
      (v: T) => {
        set(v);
        this.ctx.notifySettingsChanged();
      };

    if (this.toolId === "hole") {
      this.root.appendChild(
        holeDiameterField(s.holeRadius * 2, (newDiameter) => {
          s.holeRadius = newDiameter / 2;
          this.ctx.notifySettingsChanged();
          this.refresh();
        }),
      );
    }
    if (this.toolId === "hardware") {
      this.pieceSection.appendHardwarePanel();
    }
    if (this.toolId === "stitch") {
      // Seam kind picker (#766) at the very top — the primary "what am I
      // drawing" choice, above the chisel/spacing defaults. Polyline
      // (click-to-add) plus draggable rectangle / ellipse / slot outlines.
      this.root.appendChild(
        stitchKindGallery(
          this.stitchKind,
          (v) => {
            this.stitchKind = v;
            this.onStitchKindChange?.(v);
            this.refresh();
          },
          snapOn,
          polygonRegular,
        ),
      );
      if (this.stitchKind === "freehand") {
        this.appendLeatherFreehandSmoothness();
      }
      if (this.stitchKind === "polygon") {
        this.appendPolygonFields();
      }
      if (this.stitchKind === "slot") {
        this.root.appendChild(
          slotWidthField(
            this.stitchSlotWidth,
            (newWidth) => {
              this.stitchSlotWidth = newWidth;
              this.onStitchSlotWidthChange?.(newWidth);
              this.refresh();
            },
            "Slot width (mm) — drag to set length",
          ),
        );
      }
      if (this.stitchKind === "offset") {
        // Distance + single-edge controls, mirroring the construction offset
        // sub-tool (#768). A typed Distance fixes the inset so a click commits
        // at that value; empty → drag-defined, and the tool mirrors the live
        // distance back into this input each frame.
        const field = presetDimensionField(
          "Distance",
          this.stitchPresetOffsetDistance,
          (mm) => {
            this.stitchPresetOffsetDistance = mm;
            this.onStitchPresetOffsetDistanceChange?.(mm);
          },
        );
        this.stitchOffsetDistanceSetLive = field.setLive;
        this.root.appendChild(field.row);
        this.root.appendChild(
          checkbox(
            "Offset single edge",
            this.stitchSingleSegmentOffset,
            (on) => {
              this.stitchSingleSegmentOffset = on;
              this.onStitchSingleSegmentOffsetChange?.(on);
              this.refresh();
            },
          ),
        );
        // Hidden while single-edge is on: one edge has no corners, the same
        // reason the construction sub-tool hides its copy (#423).
        if (!this.stitchSingleSegmentOffset) {
          this.root.appendChild(this.buildOffsetRoundCornersToggle());
        }
      }
      const help = document.createElement("div");
      help.style.color = "var(--text-dim)";
      help.style.fontSize = "11px";
      help.style.marginTop = "4px";
      const clickKind =
        this.stitchKind === "polyline" || this.stitchKind === "polygon";
      help.textContent =
        this.stitchKind === "offset"
          ? this.stitchSingleSegmentOffset
            ? "Drag from a part / cutout / hole edge to offset just that edge (hold Alt to do this without the toggle)."
            : this.stitchPresetOffsetDistance !== null
              ? "Click a part edge (inset), or a cutout / hole edge (offset out into the leather), to place a seam at this distance."
              : "Drag from a part edge (inset), or a cutout / hole edge (offset out into the leather); the drag distance sets the offset. Or type a distance above."
          : clickKind
            ? this.stitchKind === "polygon"
              ? "Click points to draw a closed seam; double-click or click the start vertex to close. Hold Shift to snap segments to 45°."
              : "Click points to draw a seam; double-click or click the start vertex to finish. Hold Shift to snap segments to 45°."
            : "Drag inside a part to sweep out a seam. Hold Shift for a " +
              (this.stitchKind === "slot" || this.stitchKind === "line"
                ? "45° axis"
                : "square / circle") +
              "; Alt to draw from the center.";
      this.root.appendChild(help);
      // Chisel / spacing defaults below the kind picker — reachable while a
      // per-line / per-piece / per-scope sub-panel is also visible further down.
      this.root.appendChild(
        this.stitchSection.buildStitchingDefaultsBlock(
          "Project stitching defaults",
          "project",
        ),
      );
    }
    if (this.toolId === "construction" && this.constructionSubMode === "draw") {
      this.root.appendChild(
        constructionGallery(
          this.standardMode.construction ? "standard" : this.constructionKind,
          (v) => {
            if (v === "standard") {
              this.standardMode.construction = true;
              this.refresh();
              return;
            }
            this.standardMode.construction = false;
            this.constructionKind = v;
            this.onConstructionKindChange?.(v);
            this.refresh();
          },
          snapOn,
          polygonRegular,
        ),
      );
      if (this.constructionKind === "polygon") {
        this.appendPolygonFields();
      }
      if (this.constructionKind === "polyline") {
        this.appendPolylineFields();
      }
      if (this.constructionKind === "slot") {
        this.root.appendChild(
          slotWidthField(this.constructionSlotWidth, (newWidth) => {
            this.constructionSlotWidth = newWidth;
            this.onConstructionSlotWidthChange?.(newWidth);
            this.refresh();
          }),
        );
      }
      if (this.standardMode.construction) {
        this.appendStandardSizePanel({
          scope: "construction",
          context: "construction",
          setKind: (k) => {
            this.constructionKind = k;
            this.onConstructionKindChange?.(k);
          },
          getWidth: () => this.constructionPresetWidth,
          getHeight: () => this.constructionPresetHeight,
          setWidth: (mm) => {
            this.constructionPresetWidth = mm;
            this.onConstructionPresetWidthChange?.(mm);
          },
          setHeight: (mm) => {
            this.constructionPresetHeight = mm;
            this.onConstructionPresetHeightChange?.(mm);
          },
        });
      } else if (
        this.constructionKind === "rect" ||
        this.constructionKind === "ellipse"
      ) {
        this.appendDimensionInputs({
          isEllipse: this.constructionKind === "ellipse",
          snapOn,
          getWidth: () => this.constructionPresetWidth,
          getHeight: () => this.constructionPresetHeight,
          setWidth: (mm: number | null) => {
            this.constructionPresetWidth = mm;
            this.onConstructionPresetWidthChange?.(mm);
          },
          setHeight: (mm: number | null) => {
            this.constructionPresetHeight = mm;
            this.onConstructionPresetHeightChange?.(mm);
          },
          cornerRadiusScope: "construction",
        });
      }
      if (this.constructionKind === "line") {
        this.appendLineDrawInputs();
      }
      if (this.constructionKind === "freehand") {
        // Default smoothness for the next freehand line (issue #416).
        // Same range and re-fit semantics as the per-line "Smoothness"
        // slider, applied at draw time instead of after — and the same range
        // leather uses, since both are project millimetres (#1016).
        this.root.appendChild(
          numberField(
            "Smoothness (%)",
            smoothnessPercent(this.constructionPresetFreehandSmoothness),
            0,
            100,
            1,
            (pct) => {
              const v = smoothnessFromPercent(pct);
              this.constructionPresetFreehandSmoothness = v;
              this.onConstructionPresetFreehandSmoothnessChange?.(v);
            },
          ),
        );
      }
      const help = document.createElement("div");
      help.style.color = "var(--text-dim)";
      help.style.fontSize = "11px";
      help.style.marginTop = "4px";
      help.textContent =
        this.constructionKind === "polyline"
          ? "Click points to draw; double-click or Enter to finish. Set Segment length / Angle step to pin each segment — the angle snaps to multiples of the step, from horizontal or as the corner angle with the previous segment."
          : this.constructionKind === "polygon"
            ? "Click points to draw; double-click or click the start vertex to finish. Hold Shift to snap to 45°."
            : this.constructionKind === "line"
              ? "Drag to draw a single line. Set length to lock the line length. The angle value is a step — when set, the drag snaps to multiples of it (e.g. 30° → 0/30/60/90…)."
              : this.constructionKind === "freehand"
                ? "Drag to draw freehand. Smoothness sets how tightly the curve fits your stroke — 0 keeps every point, higher values smooth it out."
                : "Drag to draw. Hold Shift to snap.";
      this.root.appendChild(help);
    }
    if (
      this.toolId === "construction" &&
      (this.constructionSubMode === "offset" ||
        this.constructionSubMode === "simple-offset")
    ) {
      // Distance field for the offset sub-mode (#29 follow-up).
      // When the user types a value the next click commits at that
      // magnitude (the cursor's side picks the sign). Empty input
      // → drag-defined distance; the live callback mirrors the
      // current perpendicular distance into this same input each
      // frame so the user sees the value as they sweep.
      const field = presetDimensionField(
        "Distance",
        this.constructionPresetOffsetDistance,
        (mm) => {
          this.constructionPresetOffsetDistance = mm;
          this.onConstructionPresetOffsetDistanceChange?.(mm);
        },
      );
      // Bind the live-update channel to the field's setLive — the
      // tool's per-frame callback pushes the magnitude in; the
      // input stays untouched when the user has a typed preset.
      this.offsetDistanceSetLive = field.setLive;
      this.root.appendChild(field.row);
      // Single-segment offset (issue #681): offset just the one edge
      // under the cursor instead of the whole outline. Holding
      // Alt/Option during a drag does the same without the toggle.
      this.root.appendChild(
        checkbox(
          "Offset single edge",
          this.constructionSingleSegmentOffset,
          (on) => {
            this.constructionSingleSegmentOffset = on;
            this.onConstructionSingleSegmentOffsetChange?.(on);
            this.refresh();
          },
        ),
      );
      // Rounded corners for the offset about to be drawn (issue #423).
      // The derived line has carried this checkbox since #244; putting
      // it on the tool as well means the user picks the shape before
      // the drag rather than fixing it afterwards. Hidden while
      // "Offset single edge" is on — one edge has no corners to round,
      // which is the same reason the derived line's own copy hides for
      // single-edge offsets.
      if (!this.constructionSingleSegmentOffset) {
        const round = checkbox(
          "Round corners",
          this.constructionOffsetRoundedCorners,
          (on) => {
            this.constructionOffsetRoundedCorners = on;
            this.onConstructionOffsetRoundedCornersChange?.(on);
          },
        );
        // The heuristic only rounds where rounding is defined, so say
        // so rather than let the user wonder why a sharp inward offset
        // ignored the tick.
        round.title =
          "Outward offsets of sharp corners get an arc of the offset distance at each corner.";
        this.root.appendChild(round);
      }
      const help = document.createElement("div");
      help.style.color = "var(--text-dim)";
      help.style.fontSize = "11px";
      help.style.marginTop = "4px";
      help.textContent = this.constructionSingleSegmentOffset
        ? "Click one edge of a construction line to offset just that edge (hold Alt/Option to do this without the toggle)."
        : this.constructionPresetOffsetDistance !== null
          ? "Click a construction line to offset it by this distance. The side you click on picks the direction."
          : "Click a construction line and drag perpendicular to set the offset. Or type a distance above and click the line.";
      this.root.appendChild(help);
    }
    if (
      this.toolId === "construction" &&
      this.constructionSubMode === "round"
    ) {
      // Radius field for the round sub-mode (issue #100). Same opt-in
      // contract as the offset Distance: a typed value locks the next
      // click to commit at that radius; an empty input falls back to
      // drag-defined radius and the live callback mirrors the
      // in-progress drag radius into this input each frame.
      const radiusField = presetDimensionField(
        "Radius",
        this.constructionPresetCornerRadius,
        (mm) => {
          this.constructionPresetCornerRadius = mm;
          this.onConstructionPresetCornerRadiusChange?.(mm);
        },
      );
      this.cornerRadiusSetLive = radiusField.setLive;
      this.root.appendChild(radiusField.row);
      // Preset chips — common corner-rounder / corner-punch sizes the
      // user can apply with one tap. Two rows so metric users see mm
      // first and imperial users see inches first; clicking a chip
      // just sets the preset value (same as typing into the field
      // above), which the next corner click then applies.
      const setRadius = (mm: number) => {
        this.constructionPresetCornerRadius = mm;
        this.onConstructionPresetCornerRadiusChange?.(mm);
        radiusField.setValue(mm);
      };
      this.root.appendChild(
        cornerRadiusPresetRow("mm", CORNER_RADIUS_PRESETS_MM, setRadius),
      );
      this.root.appendChild(
        cornerRadiusPresetRow("in", CORNER_RADIUS_PRESETS_INCH, setRadius),
      );
      const help = document.createElement("div");
      help.style.color = "var(--text-dim)";
      help.style.fontSize = "11px";
      help.style.marginTop = "4px";
      help.textContent =
        this.constructionPresetCornerRadius !== null
          ? "Click a corner of a construction line to round it at this radius."
          : "Click a corner of a construction line and drag inward to set the radius. Or pick a preset above and click a corner.";
      this.root.appendChild(help);
    }
    if (this.toolId === "leatherRound") {
      // The leather Round method gets the SAME radius palette as the
      // construction Round sub-tool (#974) — one field plus the metric and
      // imperial corner-rounder / corner-punch chips — because it is the same
      // decision being made about the same physical tooling. Typing or picking
      // a value locks the next corner click to it; leaving it empty falls back
      // to the drag, and the live callback mirrors the in-progress radius into
      // the field each frame.
      const radiusField = presetDimensionField(
        "Radius",
        this.leatherPresetCornerRadius,
        (mm) => {
          this.leatherPresetCornerRadius = mm;
          this.onLeatherPresetCornerRadiusChange?.(mm);
        },
      );
      this.cornerRadiusSetLive = radiusField.setLive;
      this.root.appendChild(radiusField.row);
      const setRadius = (mm: number) => {
        this.leatherPresetCornerRadius = mm;
        this.onLeatherPresetCornerRadiusChange?.(mm);
        radiusField.setValue(mm);
      };
      this.root.appendChild(
        cornerRadiusPresetRow("mm", CORNER_RADIUS_PRESETS_MM, setRadius),
      );
      this.root.appendChild(
        cornerRadiusPresetRow("in", CORNER_RADIUS_PRESETS_INCH, setRadius),
      );
      const help = document.createElement("div");
      help.style.color = "var(--text-dim)";
      help.style.fontSize = "11px";
      help.style.marginTop = "4px";
      help.textContent =
        this.leatherPresetCornerRadius !== null
          ? "Click a corner of a part or cutout to round it at this radius."
          : "Drag a corner handle inward to set the radius, or pick a preset above and click a corner. All corners move together unless you turn that off in the snap toolbar.";
      this.root.appendChild(help);
    }
    if (this.toolId === "stamp" || this.toolId === "fill") {
      this.root.appendChild(
        numberField(
          this.toolId === "fill" ? "Lattice rotation (°)" : "Rotation (°)",
          this.toolId === "fill" ? s.fillRotation : s.rotation,
          -180,
          180,
          1,
          notify((v) => {
            if (this.toolId === "fill") s.fillRotation = v;
            else s.rotation = v;
          }),
        ),
      );
    }
    if (this.toolId === "lineStamp") {
      this.root.appendChild(
        lengthField(
          "Spacing",
          s.spacing,
          0,
          50,
          0.1,
          notify((v) => {
            s.spacing = v;
          }),
        ),
      );
      this.root.appendChild(
        checkbox(
          "Rotate along path",
          s.rotateAlong,
          notify((v) => {
            s.rotateAlong = v;
          }),
        ),
      );
    }
    if (this.toolId === "freePath") {
      this.root.appendChild(
        lengthField(
          "Brush size",
          s.brushSize,
          0.2,
          20,
          0.1,
          notify((v) => {
            s.brushSize = v;
          }),
        ),
      );
    }

    const sel = this.ctx.scene.getSelection();
    const piece = sel.pieceId
      ? this.ctx.history.state.pieces.find((p) => p.id === sel.pieceId)
      : undefined;

    // A tool section with no controls is just a dangling title — the pure
    // selection tools (leather Select, Construction's Select / Edit /
    // Mirror sub-modes) render nothing under it. Drop the header so the
    // panel shows only the selected object's section, or nothing (#482).
    if (this.root.childElementCount === 1) {
      title.remove();
    }
    const hasToolSection = this.root.childElementCount > 0;

    // Boundary between the tool-settings block above and the
    // selection-details block below — passed to applyStickyHeaders at the
    // end so the selected object's header pins beneath the tool header.
    const toolBoundary = this.root.childElementCount;

    // A selected note (#157). Checked before the part branches because a note
    // can point AT a part without the part being what the maker selected.
    const selectedNoteId = sel.noteIds?.[0];
    if (selectedNoteId) {
      const project = this.ctx.history.state;
      const note = (project.notes ?? []).find((n) => n.id === selectedNoteId);
      if (note) {
        renderNotePanelSection(this.root, note, {
          project,
          buildConnectionRow: (spec) => this.buildConnectionRow(spec),
          cardHeightMm: (n) =>
            noteCardHeightMm(n, this.ctx.scene.getPaperScope()),
          updateNote: (id, patch) =>
            this.ctx.commit("Edit note", (p) => ({
              ...p,
              notes: (p.notes ?? []).map((n) =>
                n.id === id ? { ...n, ...patch } : n,
              ),
            })),
          deleteNote: (id) => {
            this.ctx.commit("Delete note", (p) => ({
              ...p,
              notes: (p.notes ?? []).filter((n) => n.id !== id),
            }));
            this.ctx.scene.setSelection({
              pieceId: null,
              decorationId: null,
              noteIds: [],
            });
            this.refresh();
          },
        });
      }
    }

    if (
      sel.pieceId &&
      piece &&
      !sel.decorationId &&
      !sel.holeId &&
      !sel.cutoutId &&
      !sel.stitchId
    ) {
      this.pieceSection.appendPartPanel(piece);
    }
    if (sel.pieceId && piece && sel.holeId) {
      const hole = piece.holes.find((h) => h.id === sel.holeId);
      if (hole) this.pieceSection.appendHolePanel(piece.id, hole);
    }
    if (sel.pieceId && piece && sel.cutoutId) {
      const cut = piece.cutouts.find((c) => c.id === sel.cutoutId);
      if (cut) {
        const idx = piece.cutouts.indexOf(cut);
        this.pieceSection.appendCutoutPanel(piece.id, cut, idx);
      }
    }
    if (sel.pieceId && piece && sel.stitchId) {
      const lines = pieceStitchLines(piece);
      const line = lines.find((l) => l.id === sel.stitchId);
      if (line) {
        const idx = lines.indexOf(line);
        this.stitchSection.appendStitchPanel(piece.id, line, idx);
      }
    }
    // Project-scope selection — a construction group, a single
    // construction line, or a single tracing image (issue #509). When
    // the user has selected a group entity (Cmd+G, layers-panel click
    // on the group header, etc.) the group's panel takes precedence:
    // showing the lead line's properties beneath a group selection is
    // confusing — the user is acting on the group as an object, not on
    // one of its members. When no group is selected, fall back to the
    // line panel keyed on `constructionId`, then to the tracing-image
    // panel keyed on `tracingImageId`.
    //
    // Multi-select short-circuit (#132, extended for #509): if more
    // than one distinct entity is selected — counting top-most
    // selected groups plus every line and every image that isn't
    // already a descendant of a selected group — collapse to a single
    // "Selection" summary instead of rendering any single object's
    // full editor.
    const selectedGroupIds = sel.constructionGroupIds ?? [];
    const selectedLineIds = sel.constructionIds ?? [];
    const selectedImageIds = sel.tracingImageIds ?? [];
    const projState = this.ctx.history.state;
    const allGroups = projectConstructionGroups(projState);
    const selectedGroupRecords = selectedGroupIds
      .map((gid) => allGroups.find((g) => g.id === gid))
      .filter((g): g is ConstructionGroup => g !== undefined);
    const selectedGroupIdSet = new Set(selectedGroupRecords.map((g) => g.id));
    const topMostGroupRecords = selectedGroupRecords.filter((g) => {
      for (const aid of groupAncestorIds(projState, g.id)) {
        if (selectedGroupIdSet.has(aid)) return false;
      }
      return true;
    });
    const linesOwnedBySelectedGroups = new Set<string>();
    const imagesOwnedBySelectedGroups = new Set<string>();
    for (const g of topMostGroupRecords) {
      for (const lid of descendantConstructionLineIds(projState, g.id)) {
        linesOwnedBySelectedGroups.add(lid);
      }
      for (const iid of descendantConstructionImageIds(projState, g.id)) {
        imagesOwnedBySelectedGroups.add(iid);
      }
    }
    const looseSelectedLineIds = selectedLineIds.filter(
      (id) => !linesOwnedBySelectedGroups.has(id),
    );
    const looseSelectedImageIds = selectedImageIds.filter(
      (id) => !imagesOwnedBySelectedGroups.has(id),
    );
    const entityCount =
      topMostGroupRecords.length +
      looseSelectedLineIds.length +
      looseSelectedImageIds.length;
    if (entityCount > 1) {
      this.appendSelectionSummary(
        looseSelectedLineIds.length,
        topMostGroupRecords.length,
        looseSelectedImageIds.length,
      );
    } else if (selectedGroupRecords.length > 0) {
      this.constructionSection.appendConstructionGroupPanel(
        selectedGroupRecords,
      );
    } else if (sel.constructionId) {
      const committed = projectConstructionLines(projState).find(
        (l) => l.id === sel.constructionId,
      );
      // Prefer the live resize override (issue #388) so dimension readouts
      // track the in-progress drag; fall back to the committed line.
      const override =
        this.liveConstructionLineOverride?.id === sel.constructionId
          ? this.liveConstructionLineOverride
          : null;
      const line = override ?? committed;
      if (line) {
        this.constructionSection.appendConstructionLinePanel(line);
      }
    } else if (sel.tracingImageId) {
      // Single tracing image (issue #498 / #509): rename + read-only
      // physical size. Only when no group / line entity won above.
      const image = projectTracingImages(projState).find(
        (img) => img.id === sel.tracingImageId,
      );
      if (image) this.appendTracingImagePanel(image);
    }
    // Project-scope selection always renders the editor here. The stitch
    // tool's tool-properties block hides itself whenever any selection is
    // active (including this scope), so there's no duplicate path.
    if (sel.stitchingScope?.kind === "project") {
      this.root.appendChild(
        this.stitchSection.buildStitchingDefaultsBlock(
          "Project stitching defaults",
          "project",
        ),
      );
    } else if (sel.stitchingScope?.kind === "piece") {
      const targetPieceId = sel.stitchingScope.pieceId;
      const target = this.ctx.history.state.pieces.find(
        (p) => p.id === targetPieceId,
      );
      if (target) {
        this.root.appendChild(
          this.stitchSection.buildStitchingDefaultsBlock(
            `Stitching defaults — ${target.name}`,
            { pieceId: target.id },
          ),
        );
      }
    } else if (sel.stitchingScope?.kind === "bucket") {
      const scope = sel.stitchingScope;
      const target = this.ctx.history.state.pieces.find(
        (p) => p.id === scope.pieceId,
      );
      if (target) {
        this.root.appendChild(
          this.stitchSection.buildStitchingDefaultsBlock(
            `Stitching override — ${target.name}`,
            { pieceId: scope.pieceId, lineIds: scope.lineIds },
          ),
        );
        // Bucket-scope panel ends with a "stop overriding" button that
        // releases the lines back to the part defaults.
        const stop = document.createElement("button");
        stop.type = "button";
        stop.className = "btn";
        stop.style.width = "100%";
        stop.style.marginTop = "8px";
        stop.textContent = "Stop overriding";
        stop.addEventListener("click", () => {
          const ids = new Set(scope.lineIds);
          const proj = this.ctx.history.state;
          const piece = proj.pieces.find((p) => p.id === scope.pieceId);
          const eff = piece
            ? resolvePartStitchingDefaults(proj, piece)
            : resolveProjectStitchingDefaults(proj);
          this.ctx.commit("Drop stitch override", () => ({
            ...proj,
            pieces: proj.pieces.map((pc) =>
              pc.id !== scope.pieceId
                ? pc
                : {
                    ...pc,
                    stitchLines: pieceStitchLines(pc).map((l) =>
                      ids.has(l.id)
                        ? {
                            ...l,
                            shape: eff.shape,
                            pitch: eff.pitch,
                            size: eff.size,
                            slantAngle: eff.slantAngle,
                            overridesDefaults: false,
                          }
                        : l,
                    ),
                  },
            ),
          }));
          // Lines now live in the inherits bucket — redirect the selection
          // to the part scope so the panel doesn't go blank.
          this.ctx.setSelection({
            pieceId: null,
            decorationId: null,
            stitchingScope: { kind: "piece", pieceId: scope.pieceId },
          });
        });
        this.root.appendChild(stop);
      }
    }

    // Bottom footer: only shown when the active selection is the part itself
    // or one of its decorations. Holes / cutouts / stitch lines have their
    // own dedicated panels (with delete affordances) above, and shouldn't
    // also imply "the part is selected" — the previous logic let the
    // "Delete part" button appear whenever any sub-item was selected.
    const partOrDecorationOnly =
      !sel.stitchingScope &&
      (sel.decorationId ||
        (sel.pieceId && !sel.holeId && !sel.cutoutId && !sel.stitchId));
    if (partOrDecorationOnly) {
      const div = document.createElement("div");
      div.style.marginTop = "8px";
      div.style.color = "var(--text-dim)";
      div.style.fontSize = "11px";
      div.textContent = `Selection: part=${sel.pieceId ?? "-"} dec=${sel.decorationId ?? "-"}`;
      this.root.appendChild(div);

      if (sel.decorationId) {
        const del = document.createElement("button");
        del.className = "btn";
        del.style.marginTop = "8px";
        del.style.width = "100%";
        del.textContent = "Delete decoration";
        del.addEventListener("click", () => {
          if (!sel.pieceId || !sel.decorationId) return;
          const piece = this.ctx.history.state.pieces.find(
            (p) => p.id === sel.pieceId,
          );
          const nextDecorationId = piece
            ? pickAdjacentDecoration(piece, sel.decorationId)
            : null;
          this.ctx.commit("Delete decoration", (proj) => ({
            ...proj,
            pieces: proj.pieces.map((pc) =>
              pc.id === sel.pieceId
                ? {
                    ...pc,
                    decorations: pc.decorations.filter(
                      (d) => d.id !== sel.decorationId,
                    ),
                  }
                : pc,
            ),
          }));
          this.ctx.setSelection({
            pieceId: sel.pieceId,
            decorationId: nextDecorationId,
          });
        });
        this.root.appendChild(del);
      } else if (sel.pieceId) {
        const del = document.createElement("button");
        del.className = "btn";
        del.style.marginTop = "8px";
        del.style.width = "100%";
        del.textContent = "Delete part";
        del.addEventListener("click", () => {
          if (!sel.pieceId) return;
          this.ctx.commit("Delete part", (proj) => ({
            ...proj,
            pieces: proj.pieces.filter((pc) => pc.id !== sel.pieceId),
          }));
          this.ctx.setSelection({ pieceId: null, decorationId: null });
        });
        this.root.appendChild(del);
      }
    }

    // Measured sizes go LAST, in one block under their own heading (#155).
    // Two reasons for the position: a readout is not a control, so among the
    // fields it reads as something you can set; and one block at the foot
    // stays one block when a part AND a seam are both selected, where a
    // per-section readout would scatter the same information through the
    // panel.
    this.appendDimensionsBlock();

    // Pin the panel's two section headers as stacked sticky bars — the tool
    // header on top and the selection-block header directly beneath it — so
    // both stay visible while the sidebar scrolls. The object header's accent
    // bar (and its top margin) separates the two scopes, so no divider is
    // drawn between them.
    const objectRendered = this.root.childElementCount > toolBoundary;
    this.applyStickyHeaders(objectRendered ? toolBoundary : -1, hasToolSection);

    // …then, if the user has asked for it, move the tool scope out to the
    // sidebar of its own (#339). Deliberately the LAST thing render() does,
    // and a pure re-parent of finished DOM: every builder above keeps
    // appending into one flat root, and the sticky-header pass keeps its index
    // arithmetic over that flat list. Nothing else in the panel has to know
    // the second sidebar exists.
    this.applyToolSplit(hasToolSection && objectRendered ? toolBoundary : -1);

    // A panel with nothing at all in it — a pure selection tool and no
    // selection — renders as an empty box, so hide the shell wholesale and
    // reveal it again once either section has content (#482).
    const shell = this.root.closest(".panel-shell") as HTMLElement | null;
    if (shell) shell.hidden = this.root.childElementCount === 0;
  }

  /** The measured size of everything in the current selection, as one block at
   *  the foot of the panel. Labelled per kind, since a part and one of its
   *  seams can be selected together and "Length" alone wouldn't say whose. */
  private appendDimensionsBlock(): void {
    const sel = this.ctx.scene.getSelection();
    const project = this.ctx.history.state;
    // The units PREFERENCE, not `project.settings.units` (#1056). That field is
    // written once when a project is created and never again — nothing in the
    // app updates it — so this block rendered in millimeters however the
    // toolbar's mm / in toggle was set. Every other unit-aware readout
    // (`panelFieldBuilders`, `mmInchInputs`, the canvas dimension labels, the
    // exporters) already reads `getUnits()`; this was the one that didn't.
    const units = getUnits();
    const scope = this.ctx.scene.getPaperScope();
    const rows: string[] = [];
    const piece = sel.pieceId
      ? project.pieces.find((p) => p.id === sel.pieceId)
      : undefined;
    if (piece) {
      rows.push(
        `Part perimeter ${formatLengthMm(pathLengthMm(piece.shapePathData, scope), units)}`,
      );
      // Area, then the bounding box it sits in (#1056). A part outline is
      // always closed, so both always apply. The pair is what says whether a
      // part nests well: the gap between them IS the waste around it.
      rows.push(...areaRows("Part", piece.shapePathData, scope, units));
      const cut = sel.cutoutId
        ? piece.cutouts.find((c) => c.id === sel.cutoutId)
        : undefined;
      if (cut) {
        rows.push(
          `Cut-out perimeter ${formatLengthMm(pathLengthMm(cut.pathData, scope), units)}`,
        );
        rows.push(...areaRows("Cut-out", cut.pathData, scope, units));
      }
      const seam = sel.stitchId
        ? pieceStitchLines(piece).find((l) => l.id === sel.stitchId)
        : undefined;
      if (seam) {
        rows.push(`Seam length ${formatLengthMm(seamLengthMm(seam), units)}`);
      }
    }
    const line = sel.constructionId
      ? projectConstructionLines(project).find(
          (l) => l.id === sel.constructionId,
        )
      : undefined;
    if (line) {
      const closed = /[zZ]\s*$/.test(line.pathData);
      rows.push(
        `Line ${closed ? "perimeter" : "length"} ${formatLengthMm(pathLengthMm(line.pathData, scope), units)}`,
      );
      // Only a CLOSED line encloses anything (#1056) — an open path's "area"
      // would be whatever paper gets from silently joining its two ends, which
      // is a number about a shape the user never drew.
      if (closed) rows.push(...areaRows("Line", line.pathData, scope, units));
    }
    const block = dimensionsSection(rows);
    if (block) this.root.appendChild(block);
  }

  /** Pins the Properties-panel section headers as stacked sticky bars so
   *  they stay visible while the sidebar scrolls: the tool header ("Tool — …")
   *  sticks to the very top and the selection block's first heading sticks
   *  directly beneath it. `objectStart` is the index in `this.root` of the
   *  first selection-block child, or -1 when nothing is selected. When the
   *  tool section was dropped (no controls), the object header is the only
   *  bar — it gets the `--tool` variant so it pins flush to the top instead
   *  of leaving a gap where the tool bar would have been. */
  private applyStickyHeaders(objectStart: number, hasToolSection: boolean) {
    const toolHeader = hasToolSection
      ? (this.root.children[0] as HTMLElement | undefined)
      : undefined;
    toolHeader?.classList.add("prop-sticky-header", "prop-sticky-header--tool");
    if (objectStart < 0) return;
    // With the tool scope split into its own sidebar (#339) the object header
    // is at the top of the panel rather than under the tool one — it takes the
    // `--tool` (top: 0) variant and no stacking offset. The move happens after
    // this runs, so the header lookup below is still index-based over the flat
    // panel.
    const split = this.toolSplitActive && this.toolRoot != null;

    // First heading at/after the selection boundary — it may be a direct child
    // of the panel or wrapped one level deep (the per-selection panels vary).
    let objectHeader: HTMLElement | null = null;
    for (let i = objectStart; i < this.root.children.length; i++) {
      const child = this.root.children[i] as HTMLElement;
      if (child.tagName === "H3" || child.tagName === "H4") {
        objectHeader = child;
        break;
      }
      const nested = child.querySelector("h3, h4");
      if (nested) {
        objectHeader = nested as HTMLElement;
        break;
      }
    }
    if (!objectHeader) return;

    // Sticky needs a containing block that spans the section, but some panels
    // isolate their header in a throwaway wrapper that's exactly header-tall —
    // promote those to a direct child of the panel so the header sticks across
    // the whole selection block instead of releasing immediately.
    const parent = objectHeader.parentElement;
    if (
      parent &&
      parent.parentElement === this.root &&
      parent.childElementCount === 1
    ) {
      this.root.replaceChild(objectHeader, parent);
    }

    if (!toolHeader || split) {
      // No tool bar above it — or it went to the other sidebar — so the object
      // header pins to the top of what it heads, with the tool variant.
      objectHeader.classList.add(
        "prop-sticky-header",
        "prop-sticky-header--tool",
      );
      return;
    }
    objectHeader.classList.add(
      "prop-sticky-header",
      "prop-sticky-header--object",
    );
    // Stack the selection header directly under the tool header; measure the
    // tool header (already restyled with its sticky padding) so a wrapped or
    // restyled title still lines up exactly.
    const toolH = toolHeader.getBoundingClientRect().height;
    objectHeader.style.setProperty(
      "--prop-sticky-tool-h",
      `${toolH > 0 ? toolH : 30}px`,
    );
  }

  /**
   * Move the tool scope out into its own sidebar (#339), leaving this panel to
   * the selected object's properties. Each scope then gets a full 280px column
   * to lay its fields out in — the reason this is a sidebar and not a second
   * column inside one: splitting 280px in half leaves neither scope enough
   * width for a labelled field and its input.
   *
   * `boundary` is the index in `this.root` where the object block starts, or
   * -1 when there is only one scope on screen — in which case there is nothing
   * to separate and everything stays here. That is the common case (a tool
   * with no selection, or a selection under a pure selection tool), and
   * splitting it would open a whole sidebar to hold nothing.
   */
  private applyToolSplit(boundary: number) {
    const toolRoot = this.toolRoot;
    if (toolRoot) toolRoot.innerHTML = "";
    // Report whether there are two scopes SEPARATELY from whether we're
    // currently splitting them — a toggle that vanished whenever it was off
    // could never be turned back on.
    const splittable = boundary > 0 && toolRoot != null;
    this.onToolSplitAvailable?.(splittable);
    if (this.toolSplitActive && splittable) {
      const kids = Array.from(this.root.children);
      for (let i = 0; i < boundary; i++) toolRoot!.appendChild(kids[i]!);
    }
    // The sidebar collapses on an empty shell, the same way the properties
    // shell hides itself when both its scopes are empty (#482) — an empty
    // bordered column reads as broken.
    const toolShell = toolRoot?.closest(".panel-shell") as HTMLElement | null;
    if (toolShell) toolShell.hidden = toolRoot!.childElementCount === 0;
  }

  /** Turn the tool-settings sidebar on / off (#339) and re-render into it.
   *  Called by the shell's toggle in app.ts, which owns the preference. */
  setToolSplit(on: boolean): void {
    if (this.toolSplitActive === on) return;
    this.toolSplitActive = on;
    this.render();
  }

  /** Told after every render whether the panel HAS two scopes to separate, so
   *  the shell can hide a toggle that would do nothing. */
  setToolSplitAvailableHandler(fn: (available: boolean) => void): void {
    this.onToolSplitAvailable = fn;
  }

  /** Renders the bounding-box dimension inputs below the shape gallery for
   *  rect / ellipse kinds. Two paired text inputs (Width / Height) when snap
   *  is off, one ("Side length" for rect → square; "Diameter" for ellipse →
   *  circle) when snap is on. Each input is `presetDimensionField`, so an
   *  empty value cleanly means "read this axis from the cursor" — typing a
   *  number locks that axis for the next placement.
   *
   *  Caller passes get/set closures so the same helper drives the leather
   *  Shape tool and the Cutout tool from their separate preset state. */

  /**
   * The Standard-sizes panel (#32): a searchable, scrollable list of real-world
   * objects, plus the editable dimensions of whichever one is picked.
   *
   * A list rather than a dropdown because the catalog is meant to grow — forty
   * entries in a `<select>` is a scroll through someone else's ordering, where
   * a search box reaches "coaster" in three keystrokes.
   *
   * Picking an entry decides the SHAPE as well as the size: a round object
   * draws an ellipse, a rectangular one a rectangle. That is why this replaces
   * the shape tiles rather than sitting under them.
   */
  private appendStandardSizePanel(opts: {
    scope: "shape" | "cutout" | "construction";
    context: StandardSizeContext;
    setKind: (kind: "rect" | "ellipse") => void;
    setWidth: (mm: number | null) => void;
    setHeight: (mm: number | null) => void;
    getWidth: () => number | null;
    getHeight: () => number | null;
  }) {
    const { scope, context } = opts;
    const sizes = standardSizesInContext(context);
    const list = standardSizeList(
      sizes,
      this.standardPicked[scope],
      (size) => {
        this.standardPicked[scope] = size.id;
        // The object's own shape decides what gets drawn — a coaster that is
        // round is an ellipse, and picking it should not leave the tool on
        // rectangle.
        opts.setKind(size.round ? "ellipse" : "rect");
        opts.setWidth(size.widthMm);
        opts.setHeight(size.heightMm);
        // …and the corners it is made with (#1069): a square coaster comes
        // out rounded, with no second gesture. A size without one draws
        // sharp, so the last pick's radius doesn't linger.
        this.setDrawCornerRadius(
          scope,
          size.round ? null : (size.cornerRadiusMm ?? null),
        );
        this.refresh();
      },
      getUnits(),
    );
    this.root.appendChild(list.row);

    const picked = this.standardPicked[scope]
      ? standardSizeById(this.standardPicked[scope]!)
      : null;
    if (!picked) {
      const note = document.createElement("p");
      note.className = "field-note";
      note.dataset.role = "standard-size-hint";
      note.textContent =
        context === "leatherPiece"
          ? "Parts you'd cut whole from leather. Slots and pockets for cards, notes and trackers are cut-outs — pick Standard there instead."
          : "The object's own size. Add your own ease and turn-back — a slot cut to exactly the card doesn't take the card.";
      this.root.appendChild(note);
      return;
    }

    // The picked size's numbers, editable: the list is a shortcut to typing,
    // not a lock.
    const w = presetDimensionField(
      "Width to draw",
      opts.getWidth(),
      opts.setWidth,
    );
    const h = presetDimensionField(
      "Height to draw",
      opts.getHeight(),
      opts.setHeight,
    );
    this.root.appendChild(w.row);
    this.root.appendChild(h.row);
    this.activeWidthSetLive = w.setLive;
    this.activeHeightSetLive = h.setLive;
    if (!picked.round) this.appendDrawCornerRadiusField(scope);
    const source = document.createElement("p");
    source.className = "field-note";
    source.dataset.role = "standard-size-source";
    source.textContent = picked.hint;
    this.root.appendChild(source);
  }

  private appendDimensionInputs(opts: {
    isEllipse: boolean;
    snapOn: boolean;
    getWidth: () => number | null;
    getHeight: () => number | null;
    setWidth: (mm: number | null) => void;
    setHeight: (mm: number | null) => void;
    /** The tool whose draw-time corner radius a rectangle offers (#1069).
     *  Omitted, no Corner radius field. */
    cornerRadiusScope?: "shape" | "cutout" | "construction";
  }) {
    const { isEllipse, snapOn, getWidth, getHeight } = opts;
    // Standard object sizes (#32), above the inputs it fills: a card slot is
    // sized to a card, and looking that up every time is where a 1 mm error in
    // a card slot comes from. It writes the same presets the fields below
    // hold, so it is a shortcut to typing rather than a mode.
    const setWidth = opts.setWidth;
    const setHeight = opts.setHeight;
    if (snapOn) {
      // Snap collapses width + height into a single locked side / diameter.
      // Use whichever preset is already set (defaulting to width) so toggling
      // snap on/off doesn't surprise-reset the user's value.
      const initial = getWidth() ?? getHeight();
      // "…to draw" (#1151): these size the NEXT shape, and sit in the same
      // panel as the selected part's own Width / Height. With a tablet's
      // keyboard up only one may be on screen, so the label alone has to say
      // which one you're typing into.
      const label = isEllipse ? "Diameter to draw" : "Side length to draw";
      const field = presetDimensionField(label, initial, (mm) => {
        setWidth(mm);
        setHeight(mm);
      });
      this.root.appendChild(field.row);
      // The same setLive feeds both axes — `resolveBoundingBox` returns
      // w === h while constrainSquare is active.
      this.activeWidthSetLive = field.setLive;
      this.activeHeightSetLive = field.setLive;
    } else {
      const w = presetDimensionField("Width to draw", getWidth(), setWidth);
      const h = presetDimensionField("Height to draw", getHeight(), setHeight);
      this.root.appendChild(w.row);
      this.root.appendChild(h.row);
      this.activeWidthSetLive = w.setLive;
      this.activeHeightSetLive = h.setLive;
    }
    // A rectangle can be drawn with rounded corners (#1069).
    if (!isEllipse && opts.cornerRadiusScope) {
      this.appendDrawCornerRadiusField(opts.cornerRadiusScope);
    }
  }

  /** Tool-mode inputs for the Line construction primitive (issue
   *  #118): a length preset + a nullable angle preset with 0 / 30 /
   *  45 / 60 / 90° chips + the same crosshair icon the snap-angle
   *  chip uses, rendered live next to the angle value. Both inputs
   *  feed presets back into ConstructionTool — a null value means
   *  "follow the cursor"; a set value locks that axis for the next
   *  placement (a click without dragging still draws the line). */
  private appendLineDrawInputs() {
    const lenField = presetDimensionField(
      "Length",
      this.constructionPresetLineLength,
      (mm) => {
        this.constructionPresetLineLength = mm;
        this.onConstructionPresetLineLengthChange?.(mm);
      },
    );
    this.root.appendChild(lenField.row);
    this.activeLineLengthSetLive = lenField.setLive;

    const angleField = presetAngleField(
      this.constructionPresetLineAngleDeg,
      (deg) => {
        this.constructionPresetLineAngleDeg = deg;
        this.onConstructionPresetLineAngleChange?.(deg);
      },
      {
        label: "Angle step",
        resetTitle: "Clear the angle step",
      },
    );
    this.root.appendChild(angleField.row);
    // No live setter for the angle field — the value is a step, not
    // a fixed angle, so mirroring the cursor's current angle in it
    // would read as a misleading "the step just changed to N°". The
    // on-canvas snap-angle chip still surfaces the live line angle.
    this.activeLineAngleSetLive = null;
  }

  /** Properties for a selected tracing image (issue #498): rename it,
   *  with its calibrated physical size shown read-only for reference.
   *  Resizing/repositioning isn't exposed here yet — the image is a
   *  trace-over reference, sized from the calibration step. */
  private appendTracingImagePanel(image: TracingImage) {
    const wrap = document.createElement("div");
    const heading = document.createElement("h3");
    heading.textContent = "Tracing image";
    wrap.appendChild(heading);

    wrap.appendChild(
      textField("Name", image.name, (newName) => {
        const id = image.id;
        this.ctx.commit("Rename tracing image", (proj) => ({
          ...proj,
          tracingImages: projectTracingImages(proj).map((img) =>
            img.id === id ? { ...img, name: newName } : img,
          ),
        }));
      }),
    );

    const size = document.createElement("p");
    size.className = "field-note field-note-spaced";
    size.textContent = `${image.widthMm.toFixed(1)} × ${image.heightMm.toFixed(1)} mm`;
    wrap.appendChild(size);

    // Opacity scrubber (issue #516) — fade the reference so traced
    // geometry on top reads clearly. Scrubbing updates the on-canvas
    // raster live (no history churn); the value commits on release.
    wrap.appendChild(this.buildTracingImageOpacityField(image));

    // Rotation scrubber (issue #517) — spin the reference about its
    // centre. Live while dragging, committed on release.
    wrap.appendChild(this.buildTracingImageRotationField(image));

    this.root.appendChild(wrap);
  }

  private buildTracingImageRotationField(image: TracingImage): HTMLElement {
    const id = image.id;
    // Shared scrubber + input control (issue #529).
    const { wrap } = scrubberInputField({
      label: "Rotation",
      ariaLabel: "Tracing image rotation",
      min: -180,
      max: 180,
      step: 1,
      suffix: "°",
      value: Math.round(tracingImageRotation(image)),
      onLive: (deg) => this.ctx.scene.setTracingImageRotationLive(id, deg),
      onCommit: (deg) => {
        // Normalise to (-180, 180] so saved values stay canonical.
        let n = deg % 360;
        if (n > 180) n -= 360;
        if (n <= -180) n += 360;
        this.ctx.commit("Rotate tracing image", (proj) => ({
          ...proj,
          tracingImages: projectTracingImages(proj).map((img) =>
            img.id === id ? { ...img, rotation: n } : img,
          ),
        }));
      },
    });
    return wrap;
  }

  private buildTracingImageOpacityField(image: TracingImage): HTMLElement {
    const id = image.id;
    // Shared scrubber + input control (issue #529); the input shows the
    // percent, so there's no separate value label.
    const { wrap } = scrubberInputField({
      label: "Opacity",
      ariaLabel: "Tracing image opacity",
      min: 0,
      max: 100,
      step: 1,
      suffix: "%",
      value: Math.round(tracingImageOpacity(image) * 100),
      // Live feedback while scrubbing: set the raster opacity directly,
      // no commit (avoids a history entry per slider tick).
      onLive: (pct) => this.ctx.scene.setTracingImageOpacityLive(id, pct / 100),
      onCommit: (pct) => {
        const opacity = Math.max(0, Math.min(1, pct / 100));
        this.ctx.commit("Set tracing image opacity", (proj) => ({
          ...proj,
          tracingImages: projectTracingImages(proj).map((img) =>
            img.id === id ? { ...img, opacity } : img,
          ),
        }));
      },
    });
    return wrap;
  }

  /** A "Create construction line" button that promotes a baked object to a
   *  construction-backed one (#740). `what` names the object for the tooltip. */
  public promoteButton(what: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn";
    // Full-width with top spacing, matching the panel's other action buttons
    // (Delete part / Reset cut-layout offset / …). Flex so the construction
    // glyph and the label sit centred side by side.
    btn.style.width = "100%";
    btn.style.marginTop = "8px";
    btn.style.display = "inline-flex";
    btn.style.alignItems = "center";
    btn.style.justifyContent = "center";
    btn.style.gap = "6px";
    const icon = document.createElement("span");
    icon.setAttribute("aria-hidden", "true");
    icon.className = "promote-construction-icon";
    icon.innerHTML = CONSTRUCTION_LINE_ICON;
    btn.appendChild(icon);
    btn.appendChild(document.createTextNode("Create construction line"));
    btn.title = `Create a construction line matching ${what} so it can be edited, offset, and mirrored`;
    markPaidFeature(btn, "construction-connections");
    btn.addEventListener("click", onClick);
    return btn;
  }

  /** Multi-select summary (#132). Triggered when more than one
   *  distinct entity is selected in the construction panel — any
   *  combination of top-most groups + loose (non-group-member)
   *  lines. Replaces the per-object editor with a "Selection"
   *  block listing the counts so the user can see what they've
   *  picked without an ambiguous per-object panel implying they
   *  can edit just one of them. */
  private appendSelectionSummary(
    lineCount: number,
    groupCount: number,
    imageCount = 0,
  ) {
    const wrap = document.createElement("div");
    wrap.className = "block";
    const header = document.createElement("h4");
    header.textContent = "Selection";
    wrap.appendChild(header);
    const summary = document.createElement("div");
    summary.style.color = "var(--text-dim)";
    summary.style.fontSize = "11px";
    const parts: string[] = [];
    if (lineCount > 0) {
      parts.push(`${lineCount} line${lineCount === 1 ? "" : "s"}`);
    }
    if (imageCount > 0) {
      parts.push(`${imageCount} image${imageCount === 1 ? "" : "s"}`);
    }
    if (groupCount > 0) {
      parts.push(`${groupCount} group${groupCount === 1 ? "" : "s"}`);
    }
    summary.textContent = parts.join(", ");
    wrap.appendChild(summary);
    this.root.appendChild(wrap);
  }

  /** Build one Properties-Connections-section row from a
   *  `ConnectionRowSpec` (issue #180 unification). Shared across
   *  every connection kind so the panel owns ONE row code path
   *  instead of three. Each spec carries its own detach commit +
   *  optional Apply (constraint-only) + hover refs. */
  public buildConnectionRow(spec: ConnectionRowSpec): HTMLLIElement {
    const row = document.createElement("li");
    row.className = "constraints-row";
    if (spec.unsatisfied) {
      row.classList.add("unsatisfied");
      row.dataset.unsatisfied = "true";
    }
    row.dataset.connectionKind = spec.kind;
    row.dataset.connectionKey = spec.connectionKey;
    row.dataset.connectionRole = spec.role;
    if (spec.extraDatasetEntries) {
      for (const [key, value] of spec.extraDatasetEntries) {
        row.dataset[key] = value;
      }
    }
    // Row click (outside the Apply / × buttons) selects the OTHER side
    // of the relationship, mirroring the layers-panel chips (#221).
    const clickTarget = spec.clickTarget;
    if (clickTarget) {
      row.classList.add("constraints-row-clickable");
      row.addEventListener("click", (ev) => {
        if ((ev.target as HTMLElement).closest("button")) return;
        // Keep the click from bubbling into any ancestor's select
        // handler, which would immediately re-select the current line.
        ev.stopPropagation();
        this.ctx.setSelection({
          pieceId: clickTarget.pieceId ?? null,
          decorationId: null,
          constructionId: clickTarget.constructionId ?? null,
          holeId: null,
          cutoutId: null,
          stitchId: clickTarget.stitchId ?? null,
        });
      });
    }
    const iconHost = document.createElement("span");
    iconHost.className = "constraints-row-icon";
    iconHost.innerHTML = spec.iconSvg;
    row.appendChild(iconHost);
    const labelEl = document.createElement("span");
    labelEl.className = "constraints-row-label";
    labelEl.textContent = spec.label;
    row.appendChild(labelEl);
    if (spec.onApply) {
      const applyBtn = document.createElement("button");
      applyBtn.type = "button";
      applyBtn.className = "constraints-row-apply";
      applyBtn.title = "Apply this constraint";
      applyBtn.setAttribute("aria-label", `Apply ${spec.label} constraint`);
      applyBtn.textContent = "Apply";
      const onApply = spec.onApply;
      applyBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        onApply(this.ctx);
      });
      row.appendChild(applyBtn);
    }
    // Emergent relationships (the construction-stitch grid, #199) carry no
    // `onDetach` — they can't be broken from here, so the row is read-only.
    const onDetach = spec.onDetach;
    if (onDetach) {
      const deleteBtn = document.createElement("button");
      deleteBtn.type = "button";
      deleteBtn.className = "constraints-row-delete";
      deleteBtn.title =
        spec.kind === "snap"
          ? "Delete constraint"
          : spec.kind === "stitchMatch"
            ? "Break match"
            : `Detach ${spec.kind}`;
      deleteBtn.setAttribute(
        "aria-label",
        spec.kind === "snap"
          ? `Delete ${spec.label} constraint`
          : spec.kind === "stitchMatch"
            ? "Break stitch match"
            : `Detach ${spec.kind}`,
      );
      deleteBtn.textContent = "×";
      deleteBtn.addEventListener("click", (ev) => {
        ev.stopPropagation();
        // Clear the hover overlay first so the yellow halo doesn't
        // outlive the relationship it was pointing at.
        this.ctx.scene.setConstructionConstraintHover(null);
        onDetach(this.ctx);
      });
      row.appendChild(deleteBtn);
    }
    // Hover the row → (a) highlight every feature this connection
    // references on the canvas, and (b) cross-light every chip in
    // the layers panel that's tied to the same connection key, so
    // the parts list reflects the relationship at the same time as
    // the canvas does.
    const hoverRefs = spec.hoverRefs;
    const connectionKey = spec.connectionKey;
    const setLayersChipHighlight = (on: boolean) => {
      for (const chip of document.querySelectorAll<HTMLElement>(
        `#layers-panel [data-connection-key="${CSS.escape(connectionKey)}"]`,
      )) {
        chip.classList.toggle("related-hover", on);
      }
    };
    row.addEventListener("mouseenter", () => {
      this.ctx.scene.setConstructionConstraintHover({ refs: hoverRefs });
      setLayersChipHighlight(true);
    });
    row.addEventListener("mouseleave", () => {
      this.ctx.scene.setConstructionConstraintHover(null);
      setLayersChipHighlight(false);
    });
    return row;
  }

  /** The dual-thumb "working area" range shown on every stitch line (#199):
   *  inset the row of holes from each end. Defaults come from
   *  `resolveStitchInsets` (half a stitch / 1/8" on an open seam, 0 on a closed
   *  loop so it stitches continuously). */
  // Public so StitchPanelSection can reach it through StitchPanelHost.
  public appendWorkingAreaField(
    wrap: HTMLElement,
    pieceId: string,
    line: StitchLine,
    /** Notified with the in-flight insets on every drag + commit, so a caller
     *  (e.g. the non-follower hole-count readout) can update live. */
    onInsets?: (startInset: number, endInset: number) => void,
    /** Whether a closed loop also shows its "Start of path" control. Off for a
     *  matched follower, whose rotation is derived from its main (#692). */
    includeStartOfPath = true,
  ) {
    const total = stitchLineLength(line);
    if (!(total > 0)) return;
    const { start, end } = resolveStitchInsets(line);
    wrap.appendChild(
      stitchWorkingAreaField(
        total,
        start,
        end,
        0.05,
        // Commit (undoable) on release / typed value.
        (startInset, endInset) => {
          onInsets?.(startInset, endInset);
          this.updateStitch(pieceId, line.id, (l) => ({
            ...l,
            startInset,
            endInset,
          }));
        },
        // Live preview while dragging — re-render the canvas with the in-flight
        // insets without pushing a history entry per pointer move.
        (startInset, endInset) => {
          onInsets?.(startInset, endInset);
          this.previewStitchPatch(pieceId, line.id, { startInset, endInset });
        },
      ),
    );

    // A closed loop also gets a "Start of path" control — where the stitching
    // begins counting from (the working area is measured from there), with
    // cardinal tick-marks the user can snap to.
    if (line.closed === true && includeStartOfPath) {
      wrap.appendChild(
        stitchStartOfPathField(
          total,
          line.startOffset ?? 0,
          stitchCardinalTicks(line),
          0.05,
          (startOffset) =>
            this.updateStitch(pieceId, line.id, (l) => ({ ...l, startOffset })),
          (startOffset) =>
            this.previewStitchPatch(pieceId, line.id, { startOffset }),
        ),
      );
    }
  }

  /** Render the canvas with a patch applied to one stitch line, without
   *  committing — the live preview behind a working-area / start-of-path drag
   *  (#199). Re-runs the same seam-relationship passes as the commit pipeline so
   *  dependents update live: `deriveConstructionStitchGrid` re-aligns the grid
   *  followers when the LEADER's Start moves (dragging it shifts the whole shared
   *  row in real time, not just on release), and `deriveStitchMatches` re-stitches
   *  a matched main's followers. */
  private previewStitchPatch(
    pieceId: string,
    stitchId: string,
    patch: Partial<StitchLine>,
  ) {
    const proj = this.ctx.history.state;
    const preview: Project = {
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              stitchLines: pieceStitchLines(pc).map((l) =>
                l.id === stitchId ? { ...l, ...patch } : l,
              ),
            },
      ),
    };
    const gridded =
      isFeatureEnabled("stitch-matching") &&
      isFeatureEnabled("construction-connections")
        ? deriveConstructionStitchGrid(preview, this.ctx.scene.getPaperScope())
        : preview;
    this.ctx.scene.render(deriveStitchMatches(gridded));
  }

  // Public so StitchPanelSection can reach it through StitchPanelHost.
  public updateStitch(
    pieceId: string,
    stitchId: string,
    fn: (l: StitchLine) => StitchLine,
  ) {
    this.ctx.commit("Edit stitch line", (proj) => ({
      ...proj,
      pieces: proj.pieces.map((pc) =>
        pc.id !== pieceId
          ? pc
          : {
              ...pc,
              stitchLines: pieceStitchLines(pc).map((l) =>
                l.id === stitchId ? fn(l) : l,
              ),
            },
      ),
    }));
  }
}

/** Common metric corner-punch / corner-knife radii in millimetres. */
const CORNER_RADIUS_PRESETS_MM = [2, 3, 4, 5, 6, 8, 10, 12];
/** Common imperial corner-rounder sizes (label → radius in mm). */
const CORNER_RADIUS_PRESETS_INCH: Array<{ label: string; value: number }> = [
  { label: '1/16"', value: 1.5875 },
  { label: '1/8"', value: 3.175 },
  { label: '3/16"', value: 4.7625 },
  { label: '1/4"', value: 6.35 },
  { label: '5/16"', value: 7.9375 },
  { label: '3/8"', value: 9.525 },
  { label: '1/2"', value: 12.7 },
];

/** English ordinal suffix: 1→"1st", 2→"2nd", 3→"3rd", 4→"4th", 11→"11th"… */
export function ordinal(n: number): string {
  const v = Math.abs(n) % 100;
  const suffix =
    v >= 11 && v <= 13
      ? "th"
      : (["th", "st", "nd", "rd"][Math.abs(n) % 10] ?? "th");
  return `${n}${suffix}`;
}

/** Bucket key that mirrors LayersPanel's grouping — kept in sync with the
 *  serialization there so a line promoted to "override" lands in the same
 *  bucket the layers panel will display. */
function stitchBucketKey(line: StitchLine): string {
  if (line.overridesDefaults === false) return "inherits";
  return `over:${line.shape}:${line.pitch.toFixed(3)}:${line.size.toFixed(3)}:${line.slantAngle.toFixed(1)}`;
}

/** Return all line IDs in `piece` that share a bucket with the given line —
 *  i.e., the line plus any that already overrode to identical settings.
 *  Used to seed a bucket-scope selection when a line gets promoted, so the
 *  panel transitions onto the new (or joined) bucket cleanly. */
export function bucketmateLineIds(piece: Piece, lineId: string): string[] {
  const lines = pieceStitchLines(piece);
  const target = lines.find((l) => l.id === lineId);
  if (!target) return [lineId];
  const targetKey = stitchBucketKey(target);
  return lines.filter((l) => stitchBucketKey(l) === targetKey).map((l) => l.id);
}
